import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
    LayoutDashboard, Package, ShoppingCart, Users, FolderTree, 
    Plus, Edit2, Trash2, ChevronLeft, Eye, EyeOff, Settings,
    Banknote, Upload, Star, Sparkles, TrendingUp, Image, ArrowRightLeft, Check, X, RefreshCw, Copy, Zap, Percent, Kanban, List,
    ChevronDown, ChevronRight, Folder, FolderOpen, Truck, MapPin, FileText, Calendar, ImagePlus, Loader2, Tags, ArrowUp, ArrowDown, Search, Download, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import KanbanOrdersBoard from '../components/KanbanOrdersBoard';
import BrandsManager from '../components/admin/BrandsManager';
import PromotionsManager from '../components/admin/PromotionsManager';
import BackupManager from '../components/admin/BackupManager';
import ICashManager from '../components/admin/ICashManager';
import CategoryBrandsSection from '../components/admin/CategoryBrandsSection';
import ProductOrderManager from '../components/admin/ProductOrderManager';
import BulkImportDialog from '../components/admin/BulkImportDialog';
import DuplicatesManager from '../components/admin/DuplicatesManager';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Turn backend/validation errors into a specific, human-readable Bulgarian message
const FIELD_LABELS_BG = {
    name: 'Име', price: 'Цена', category_id: 'Категория', subcategory: 'Подкатегория',
    brand_id: 'Марка', brand: 'Марка', stock: 'Наличност', image_url: 'Снимка',
    barcode: 'Баркод', sku: 'SKU', weight: 'Тегло', description: 'Описание',
    promotional_price: 'Промо цена', discount_percent: 'Отстъпка'
};
const formatApiError = (error, fallback = 'Грешка при запазване. Опитайте отново.') => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        const parts = detail.slice(0, 4).map(d => {
            const field = (d.loc || []).filter(x => x !== 'body').join('.');
            const label = FIELD_LABELS_BG[field] || field || 'поле';
            if (d.type === 'missing') return `Липсва: ${label}`;
            if ((d.type || '').includes('float') || (d.type || '').includes('int')) return `Невалидна стойност за: ${label}`;
            return `${label}: ${d.msg || 'невалидна стойност'}`;
        });
        return parts.join(' · ');
    }
    if (error?.response?.status >= 500) return 'Сървърна грешка при запазване — опитайте отново или се свържете с поддръжката.';
    if (error?.message === 'Network Error') return 'Няма връзка със сървъра — проверете интернета.';
    return fallback;
};

// Admin Layout
const AdminLayout = ({ children }) => {
    const { user, isAdmin, token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [notifications, setNotifications] = useState({
        new_orders: 0,
        processing_orders: 0,
        total_pending_orders: 0,
        draft_products: 0,
        draft_categories: 0,
        low_stock_alerts: 0
    });
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('admin_sidebar_collapsed') === '1');
    const toggleSidebar = () => {
        setSidebarCollapsed(prev => {
            localStorage.setItem('admin_sidebar_collapsed', prev ? '0' : '1');
            return !prev;
        });
    };

    useEffect(() => {
        if (!isAdmin) {
            navigate('/');
        }
    }, [isAdmin, navigate]);

    // Fetch notifications
    useEffect(() => {
        if (token && isAdmin) {
            const fetchNotifications = async () => {
                try {
                    const response = await axios.get(`${API}/admin/notifications`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setNotifications(response.data);
                } catch (error) {
                    console.error('Error fetching notifications:', error);
                }
            };
            fetchNotifications();
            // Refresh every 30 seconds
            const interval = setInterval(fetchNotifications, 30000);
            return () => clearInterval(interval);
        }
    }, [token, isAdmin]);

    if (!isAdmin) return null;

    const navItems = [
        { path: '/admin', icon: LayoutDashboard, label: 'Табло' },
        { path: '/admin/products', icon: Package, label: 'Продукти', badge: notifications.draft_products },
        { path: '/admin/orders', icon: ShoppingCart, label: 'Поръчки', badge: notifications.total_pending_orders, badgeColor: 'bg-red-500' },
        { path: '/admin/categories', icon: FolderTree, label: 'Категории', badge: notifications.draft_categories },
        { path: '/admin/brands', icon: Tags, label: 'Марки' },
        { path: '/admin/promotions', icon: Percent, label: 'Промоции' },
        { path: '/admin/backup', icon: Download, label: 'Резервни копия' },
        { path: '/admin/icash', icon: RefreshCw, label: 'iCash синхр.' },
        { path: '/admin/users', icon: Users, label: 'Потребители' },
        { path: '/admin/bank', icon: Banknote, label: 'Банкови данни' },
        { path: '/admin/delivery', icon: Truck, label: 'Доставка' },
    ];

    return (
        <div className="min-h-screen bg-background dark:bg-slate-900">
            <div className="flex">
                {/* Sidebar */}
                <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} min-h-screen bg-white dark:bg-slate-800 border-r border-border dark:border-slate-700 ${sidebarCollapsed ? 'p-2' : 'p-4'} fixed z-20 transition-all duration-200`}>
                    <div className="flex items-center justify-between mb-6">
                        {!sidebarCollapsed && (
                            <Link to="/" className="flex items-center gap-2 min-w-0">
                                <ChevronLeft className="h-4 w-4 shrink-0 dark:text-slate-300" />
                                <span className="font-serif text-lg font-bold text-primary dark:text-amber-400 truncate">Вкусът на Турция</span>
                            </Link>
                        )}
                        <button
                            onClick={toggleSidebar}
                            title={sidebarCollapsed ? 'Разгъни менюто' : 'Свий менюто'}
                            data-testid="sidebar-toggle-btn"
                            className={`p-2 rounded-lg hover:bg-amber-50 dark:hover:bg-slate-700 text-muted-foreground ${sidebarCollapsed ? 'mx-auto' : ''}`}
                        >
                            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                        </button>
                    </div>

                    <nav className="space-y-1">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path || 
                                (item.path !== '/admin' && location.pathname.startsWith(item.path));
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    title={item.label}
                                    data-testid={`admin-nav-${item.path.split('/').pop() || 'dashboard'}`}
                                    className={`flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-3 rounded-lg transition-colors relative ${
                                        isActive 
                                            ? 'bg-primary text-primary-foreground dark:bg-amber-600 dark:text-white' 
                                            : 'text-foreground dark:text-slate-300 hover:bg-muted dark:hover:bg-slate-700'
                                    }`}
                                >
                                    <item.icon className="h-5 w-5 shrink-0" />
                                    {!sidebarCollapsed && item.label}
                                    {item.badge > 0 && (
                                        <span className={`absolute ${sidebarCollapsed ? 'top-0.5 right-0.5 min-w-[16px] h-4 text-[10px]' : 'right-3 min-w-[20px] h-5 text-xs'} px-1 flex items-center justify-center font-bold text-white rounded-full ${item.badgeColor || 'bg-amber-500'} animate-pulse`}>
                                            {item.badge > 99 ? '99+' : item.badge}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </nav>
                    
                    {/* Low stock alert */}
                    {notifications.low_stock_alerts > 0 && !sidebarCollapsed && (
                        <div className="mt-6 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                            <p className="text-xs font-medium text-red-700 dark:text-red-400">
                                ⚠️ {notifications.low_stock_alerts} продукта с ниска наличност
                            </p>
                        </div>
                    )}
                </aside>

                {/* Main Content */}
                <main className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} min-w-0 p-4 lg:p-6 dark:bg-slate-900 transition-all duration-200`}>
                    <div className="max-w-[1500px]">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

// Dashboard
const AdminDashboard = () => {
    const { token } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recentOrders, setRecentOrders] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [lowStockProducts, setLowStockProducts] = useState([]);
    const [maintenanceOn, setMaintenanceOn] = useState(false);
    const [maintenanceSaving, setMaintenanceSaving] = useState(false);

    useEffect(() => {
        axios.get(`${API}/maintenance`).then(res => setMaintenanceOn(!!res.data.enabled)).catch(() => {});
    }, []);

    const toggleMaintenance = async (value) => {
        setMaintenanceSaving(true);
        try {
            await axios.post(`${API}/admin/maintenance`, { enabled: value }, { headers: { Authorization: `Bearer ${token}` } });
            setMaintenanceOn(value);
            toast.success(value ? 'Магазинът е СПРЯН — клиентите виждат страница „Поддръжка“' : 'Магазинът отново работи нормално');
        } catch (e) {
            toast.error('Грешка при промяна на режима');
        } finally {
            setMaintenanceSaving(false);
        }
    };

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await axios.get(`${API}/admin/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(response.data);
                setRecentOrders(response.data.recent_orders || []);
                setTopProducts(response.data.top_products || []);
                setLowStockProducts(response.data.low_stock_products || []);
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [token]);

    if (loading) {
        return <div className="animate-pulse space-y-6">
            <div className="h-10 w-48 bg-amber-100 dark:bg-slate-700 rounded-xl" />
            <div className="grid grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-36 bg-amber-100 dark:bg-slate-700 rounded-2xl" />)}
            </div>
            <div className="grid grid-cols-2 gap-6">
                {[1, 2].map(i => <div key={i} className="h-64 bg-amber-100 dark:bg-slate-700 rounded-2xl" />)}
            </div>
        </div>;
    }

    const statCards = [
        { 
            label: 'Общо поръчки', 
            value: stats?.total_orders || 0, 
            icon: ShoppingCart, 
            color: 'from-blue-500 to-indigo-500',
            bgColor: 'bg-blue-50 dark:bg-blue-900/30',
            change: '+12%'
        },
        { 
            label: 'Общо продукти', 
            value: stats?.total_products || 0,
            icon: Package, 
            color: 'from-amber-500 to-orange-500',
            bgColor: 'bg-amber-50 dark:bg-amber-900/30',
            change: '+5'
        },
        { 
            label: 'Потребители', 
            value: stats?.total_users || 0, 
            icon: Users, 
            color: 'from-green-500 to-emerald-500',
            bgColor: 'bg-green-50 dark:bg-green-900/30',
            change: '+8'
        },
        { 
            label: 'Приходи', 
            value: `${getPriceData(stats?.total_revenue || 0).eurFormatted} €`, 
            icon: TrendingUp, 
            color: 'from-purple-500 to-pink-500',
            bgColor: 'bg-purple-50 dark:bg-purple-900/30',
            change: '+23%'
        },
    ];

    const orderStatuses = {
        'pending': { label: 'Чакащи', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' },
        'awaiting_prepayment': { label: 'Очаква плащане', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300' },
        'processing': { label: 'В обработка', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' },
        'shipped': { label: 'Изпратени', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300' },
        'delivered': { label: 'Доставени', color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' },
        'cancelled': { label: 'Отказани', color: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' },
    };

    return (
        <div className="space-y-8">
            {/* Maintenance mode banner/toggle */}
            <div className={`rounded-2xl border p-4 flex items-center justify-between flex-wrap gap-3 ${maintenanceOn ? 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800' : 'bg-white dark:bg-slate-800 border-border dark:border-slate-700'}`} data-testid="maintenance-toggle-card">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${maintenanceOn ? 'bg-red-500' : 'bg-emerald-500'}`}>
                        {maintenanceOn ? <AlertTriangle className="w-5 h-5 text-white" /> : <Check className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                        <p className="font-semibold dark:text-white">
                            {maintenanceOn ? 'Магазинът е СПРЯН (режим на поддръжка)' : 'Магазинът работи нормално'}
                        </p>
                        <p className="text-xs text-muted-foreground dark:text-slate-400">
                            {maintenanceOn ? 'Клиентите виждат страница „Поддръжка“, поръчките са блокирани. Админът остава достъпен.' : 'Включете режима на поддръжка, за да спрете временно магазина за клиенти.'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium dark:text-slate-300">{maintenanceOn ? 'Спрян' : 'Активен'}</span>
                    <Switch checked={maintenanceOn} onCheckedChange={toggleMaintenance} disabled={maintenanceSaving} data-testid="maintenance-switch" />
                </div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-serif text-3xl font-bold text-slate-800 dark:text-white">Табло</h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">Добре дошли в администраторския панел</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Export Dropdown */}
                    <div className="relative group">
                        <Button 
                            variant="outline" 
                            size="sm"
                            className="dark:border-slate-600 dark:text-slate-300"
                        >
                            📊 Експорт
                        </Button>
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                            <div className="py-2">
                                <p className="px-4 py-1 text-xs text-slate-500 dark:text-slate-400 font-semibold">EXCEL (.xlsx)</p>
                                <a href={`${API}/admin/export/orders?format=excel`} className="block px-4 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-700">📦 Поръчки</a>
                                <a href={`${API}/admin/export/products?format=excel`} className="block px-4 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-700">🛍️ Продукти</a>
                                <a href={`${API}/admin/export/customers?format=excel`} className="block px-4 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-700">👥 Клиенти</a>
                                <hr className="my-2 border-slate-200 dark:border-slate-700" />
                                <p className="px-4 py-1 text-xs text-slate-500 dark:text-slate-400 font-semibold">CSV (.csv)</p>
                                <a href={`${API}/admin/export/orders?format=csv`} className="block px-4 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-700">📦 Поръчки</a>
                                <a href={`${API}/admin/export/products?format=csv`} className="block px-4 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-700">🛍️ Продукти</a>
                                <a href={`${API}/admin/export/customers?format=csv`} className="block px-4 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-700">👥 Клиенти</a>
                            </div>
                        </div>
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={async () => {
                            try {
                                const response = await axios.post(`${API}/admin/test-email`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success(response.data.message || 'Тестов имейл изпратен!');
                            } catch (error) {
                                toast.error(error.response?.data?.detail || 'Грешка при изпращане');
                            }
                        }}
                        className="dark:border-slate-600 dark:text-slate-300"
                    >
                        📧 Тест имейл
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={async () => {
                            const phone = window.prompt('Въведете телефонен номер за тест SMS (напр. +359888123456):');
                            if (!phone) return;
                            try {
                                const response = await axios.post(`${API}/admin/test-sms`, { phone }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success(response.data.message || 'Тестов SMS изпратен!');
                            } catch (error) {
                                toast.error(error.response?.data?.detail || 'Грешка при изпращане на SMS');
                            }
                        }}
                        className="dark:border-slate-600 dark:text-slate-300"
                    >
                        📱 Тест SMS
                    </Button>
                    <span className="px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full text-sm font-medium flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        Онлайн
                    </span>
                </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, i) => (
                    <div key={i} className={`${stat.bgColor} p-6 rounded-2xl border border-white/50 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1`}>
                        <div className="flex items-start justify-between mb-4">
                            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                                <stat.icon className="h-7 w-7 text-white" />
                            </div>
                            <span className="px-2 py-1 bg-white/80 dark:bg-slate-800 text-green-600 dark:text-green-400 text-xs font-bold rounded-lg">
                                {stat.change}
                            </span>
                        </div>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">{stat.label}</p>
                        <p className="text-3xl font-bold text-slate-800 dark:text-white mt-1">{stat.value}</p>
                    </div>
                ))}
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Orders */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-amber-100 dark:border-slate-700 shadow-lg overflow-hidden">
                    <div className="p-6 border-b border-amber-100 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-slate-800 dark:to-slate-800">
                        <div className="flex items-center justify-between">
                            <h2 className="font-serif text-xl font-bold text-slate-800 dark:text-white">Последни поръчки</h2>
                            <Link to="/admin/orders" className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 text-sm font-medium">
                                Виж всички →
                            </Link>
                        </div>
                    </div>
                    <div className="p-4">
                        {recentOrders.length > 0 ? (
                            <div className="space-y-3">
                                {recentOrders.slice(0, 5).map((order) => (
                                    <div key={order.order_id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                                                #{order.order_id?.toString().slice(-2) || '00'}
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-800 dark:text-white">Поръчка #{order.order_id}</p>
                                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                                    {new Date(order.created_at).toLocaleDateString('bg-BG')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-slate-800 dark:text-white">{getPriceData(order.total || 0).eurFormatted} €</p>
                                            <Badge className={orderStatuses[order.status]?.color || 'bg-slate-100 dark:bg-slate-600'}>
                                                {orderStatuses[order.status]?.label || order.status}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                                <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>Няма поръчки все още</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Low Stock Alert */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-amber-100 dark:border-slate-700 shadow-lg overflow-hidden">
                    <div className="p-6 border-b border-amber-100 dark:border-slate-700 bg-gradient-to-r from-red-50 to-orange-50 dark:from-slate-800 dark:to-slate-800">
                        <div className="flex items-center justify-between">
                            <h2 className="font-serif text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                Ниска наличност
                            </h2>
                            <Link to="/admin/products" className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 text-sm font-medium">
                                Виж всички →
                            </Link>
                        </div>
                    </div>
                    <div className="p-4">
                        {lowStockProducts.length > 0 ? (
                            <div className="space-y-3">
                                {lowStockProducts.slice(0, 5).map((product) => (
                                    <div key={product.product_id} className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                                        <div className="flex items-center gap-4">
                                            <img 
                                                src={product.image_url} 
                                                alt={product.name}
                                                className="w-12 h-12 rounded-lg object-cover"
                                            />
                                            <div>
                                                <p className="font-medium text-slate-800 dark:text-white line-clamp-1">{product.name}</p>
                                                <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                                                    Остават: {product.stock} бр.
                                                </p>
                                            </div>
                                        </div>
                                        <Button size="sm" variant="outline" className="border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30">
                                            Зареди
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>Всички продукти са заредени</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Top Products */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-amber-100 dark:border-slate-700 shadow-lg overflow-hidden">
                <div className="p-6 border-b border-amber-100 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-slate-800 dark:to-slate-800">
                    <h2 className="font-serif text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                        <Star className="h-5 w-5 text-amber-500" />
                        Топ продаващи се продукти
                    </h2>
                </div>
                <div className="p-6">
                    {topProducts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {topProducts.slice(0, 5).map((product, i) => (
                                <div key={product.product_id} className="relative bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-700 dark:to-slate-700 p-4 rounded-xl text-center hover:shadow-lg transition-all">
                                    <span className="absolute top-2 left-2 w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                                        {i + 1}
                                    </span>
                                    <img 
                                        src={product.image_url} 
                                        alt={product.name}
                                        className="w-20 h-20 rounded-xl object-cover mx-auto mb-3"
                                    />
                                    <p className="font-medium text-slate-800 dark:text-white line-clamp-2 text-sm mb-1">{product.name}</p>
                                    <p className="text-amber-600 dark:text-amber-400 font-bold">{getPriceData(product.price || 0).eurFormatted} €</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{product.sales_count || 0} продажби</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                            <Star className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>Няма данни за продажби</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Products Management with Image Upload
const AdminProducts = () => {
    const { token } = useAuth();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    // New states for category filtering and bulk move
    const [filterCategory, setFilterCategory] = useState('all');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showMoveDialog, setShowMoveDialog] = useState(false);
    const [targetCategory, setTargetCategory] = useState('');
    const [targetSubcategory, setTargetSubcategory] = useState('');
    
    // NEW: Quick mode and bulk edit states
    const [quickMode, setQuickMode] = useState(false);
    const [showBulkPriceDialog, setShowBulkPriceDialog] = useState(false);
    const [bulkPriceAction, setBulkPriceAction] = useState('percent'); // 'percent' or 'fixed'
    const [bulkPriceValue, setBulkPriceValue] = useState('');
    const [inlineEdit, setInlineEdit] = useState({ productId: null, field: null, value: '' });

    // NEW: Bulk image import → draft products (extracted component)
    const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);

    // NEW: Duplicates manager
    const [showDuplicates, setShowDuplicates] = useState(false);
    const [duplicatesCount, setDuplicatesCount] = useState(0);

    // NEW: Selected AI Fill + Progress Panel
    const [showAiSelectedDialog, setShowAiSelectedDialog] = useState(false);
    const [aiSelectedOverwrite, setAiSelectedOverwrite] = useState(false);
    const [aiSelectedStarting, setAiSelectedStarting] = useState(false);
    const [showAiProgressPanel, setShowAiProgressPanel] = useState(false);
    const [aiProgressErrorsOnly, setAiProgressErrorsOnly] = useState(false);
    const [aiStopping, setAiStopping] = useState(false);

    // NEW: Bulk delete confirmation
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    // NEW: Bulk stock dialog
    const [showBulkStockDialog, setShowBulkStockDialog] = useState(false);
    const [bulkStockValue, setBulkStockValue] = useState('');
    const [bulkStockSaving, setBulkStockSaving] = useState(false);
    
    // NEW: Folder view mode
    const [productsViewMode, setProductsViewMode] = useState('folders'); // 'folders' or 'list'
    const [expandedCategories, setExpandedCategories] = useState({});
    const [expandedSubcategories, setExpandedSubcategories] = useState({});
    
    // Pagination for list view
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 10; // По-малко продукти за по-големи preview-та
    
    // Scroll position preservation
    const scrollPositionRef = useRef(0);
    
    // Product templates
    const productTemplates = [
        { id: 'sweets', name: '🍬 Турски сладки', category_id: 'cat_sweets', description: 'Автентични турски сладки, приготвени по традиционна рецепта.' },
        { id: 'food', name: '🥫 Хранителен продукт', category_id: 'cat_food', description: 'Качествен хранителен продукт от Турция.' },
        { id: 'cleaning', name: '🧴 Перилен препарат', category_id: 'cat_cleaning', description: 'Ефективен почистващ препарат за вашия дом.' },
        { id: 'nuts', name: '🥜 Ядки', category_id: 'cat_nuts', description: 'Хрупкави и вкусни ядки, внимателно подбрани.' },
    ];
    
    const EXTRA_FIELDS_DEFAULTS = {
        original_name: '', short_description: '', composition: '', allergens: '',
        nutrition: '', usage_instructions: '', warnings: '', origin_country: '',
        barcode: '', sku: '', tags: '', seo_title: '', seo_description: '', slug: ''
    };

    const [formData, setFormData] = useState({
        name: '', description: '', price: '', promotional_price: '', 
        category_id: '', category_ids: [], category_subcategories: {}, subcategory: '', sub_subcategory: '',
        brand: '', brand_id: null, image_url: '', stock: '', is_featured: false, is_new: false,
        is_bestseller: false, is_visible: true, is_draft: false, discount_percent: '',
        weight: '', weight_unit: 'гр', weight_visible: false, // грамаж/литри
        requires_prepayment_outside_sofia: false,
        ...EXTRA_FIELDS_DEFAULTS
    });
    const [showExtraFields, setShowExtraFields] = useState(false);

    // Brands for product form
    const [brands, setBrands] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);
    const [brandsFiltered, setBrandsFiltered] = useState(false);
    const [showNewBrand, setShowNewBrand] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');
    const [filterNoBrand, setFilterNoBrand] = useState(false);
    const [filterBrand, setFilterBrand] = useState('all');
    const [expandedBrandFolders, setExpandedBrandFolders] = useState({});
    const [showBulkBrandDialog, setShowBulkBrandDialog] = useState(false);
    const [bulkBrandValue, setBulkBrandValue] = useState('');
    const [bulkBrandSaving, setBulkBrandSaving] = useState(false);
    const [autoAssignRunning, setAutoAssignRunning] = useState(false);
    const [autoAssignReport, setAutoAssignReport] = useState(null);
    const [productSearch, setProductSearch] = useState('');
    const [filterNoBarcode, setFilterNoBarcode] = useState(false);
    const [showOrderManager, setShowOrderManager] = useState(false);

    useEffect(() => {
        const t = token || localStorage.getItem('token');
        if (!t) return;
        axios.get(`${API}/admin/brands`, { headers: { Authorization: `Bearer ${t}` } })
            .then(res => setBrands(res.data.brands || []))
            .catch(() => {});
    }, [token]);

    // Brands filtered by the product's category/subcategory (Category → Subcategory → Brand)
    useEffect(() => {
        const t = token || localStorage.getItem('token');
        if (!t) return;
        const params = {};
        if (formData.category_id) params.category_id = formData.category_id;
        if (formData.subcategory) params.subcategory = formData.subcategory;
        axios.get(`${API}/admin/brands/for-product`, { params, headers: { Authorization: `Bearer ${t}` } })
            .then(res => {
                setBrandOptions(res.data.brands || []);
                setBrandsFiltered(!!res.data.filtered);
            })
            .catch(() => { setBrandOptions([]); setBrandsFiltered(false); });
    }, [token, formData.category_id, formData.subcategory]);

    const createBrandInline = async () => {
        const name = newBrandName.trim();
        if (!name) return;
        try {
            const links = formData.category_id
                ? [{ category_id: formData.category_id, subcategories: formData.subcategory ? [formData.subcategory] : [] }]
                : [];
            const res = await axios.post(`${API}/admin/brands`, { name, links }, { headers: { Authorization: `Bearer ${token}` } });
            const b = res.data.brand;
            if (res.data.existing) toast.info(`Марката "${b.name}" вече съществува — избрана е и е свързана с категорията`);
            else toast.success(`Марката "${b.name}" е създадена и свързана с категорията`);
            setBrands(prev => prev.some(x => x.brand_id === b.brand_id) ? prev : [...prev, b].sort((a, c) => (a.name || '').localeCompare(c.name || '')));
            setBrandOptions(prev => prev.some(x => x.brand_id === b.brand_id) ? prev : [...prev, b].sort((a, c) => (a.name || '').localeCompare(c.name || '')));
            setFormData(prev => ({ ...prev, brand_id: b.brand_id, brand: b.name }));
            setNewBrandName('');
            setShowNewBrand(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при създаване на марка');
        }
    };

    useEffect(() => {
        const tokenToUse = token || localStorage.getItem('token');
        if (!tokenToUse) return;
        
        const controller = new AbortController();
        
        fetch(`${API}/admin/products?limit=5000`, {
            headers: { 'Authorization': `Bearer ${tokenToUse}` },
            signal: controller.signal
        })
        .then(res => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        })
        .then(data => {
            const list = Array.isArray(data.products) ? data.products : (Array.isArray(data) ? data : []);
            setProducts(list);
            setLoading(false);
        })
        .catch(err => {
            if (err.name !== 'AbortError') {
                console.error('Fetch error:', err);
                toast.error('Грешка при зареждане на продуктите. Опитайте с бутона за обновяване.');
                setLoading(false);
            }
        });
        
        fetch(`${API}/categories`, { signal: controller.signal })
            .then(res => res.json())
            .then(data => setCategories(data))
            .catch(() => {});
        
        return () => controller.abort();
    }, [token]);

    // Separate fetchData for manual refresh calls
    const fetchData = async () => {
        const tokenToUse = token || localStorage.getItem('token');
        if (!tokenToUse) return;
        
        try {
            const [prodRes, catRes] = await Promise.all([
                axios.get(`${API}/admin/products`, { 
                    params: { limit: 5000 },
                    headers: { Authorization: `Bearer ${tokenToUse}` }
                }),
                axios.get(`${API}/categories`)
            ]);
            const list = Array.isArray(prodRes.data.products) ? prodRes.data.products : (Array.isArray(prodRes.data) ? prodRes.data : []);
            setProducts(list);
            setCategories(catRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            try {
                const [prodRes, catRes] = await Promise.all([
                    axios.get(`${API}/products`, { params: { limit: 5000 } }),
                    axios.get(`${API}/categories`)
                ]);
                setProducts(prodRes.data.products);
                setCategories(catRes.data);
            } catch (fallbackError) {
                console.error('Fallback error:', fallbackError);
                toast.error('Грешка при зареждане на данните');
            }
        } finally {
            setLoading(false);
        }
    };

    // === AI Draft Assistant ===
    const [aiFill, setAiFill] = useState(null);
    const [aiLoadingId, setAiLoadingId] = useState(null);
    const aiPollRef = useRef(null);

    useEffect(() => () => clearInterval(aiPollRef.current), []);

    const pollAiFill = () => {
        clearInterval(aiPollRef.current);
        aiPollRef.current = setInterval(async () => {
            try {
                const s = await axios.get(`${API}/admin/ai-fill-drafts/status`, {
                    headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
                });
                setAiFill(s.data);
                if (!s.data.running) {
                    clearInterval(aiPollRef.current);
                    toast.success(`✨ AI попълни ${s.data.done - s.data.errors} чернови${s.data.errors ? `, ${s.data.errors} грешки` : ''}`);
                    fetchData();
                    fetchDuplicatesCount();
                }
            } catch (e) { /* keep polling */ }
        }, 3000);
    };

    useEffect(() => {
        const t = token || localStorage.getItem('token');
        if (!t) return;
        axios.get(`${API}/admin/ai-fill-drafts/status`, { headers: { Authorization: `Bearer ${t}` } })
            .then(res => { if (res.data.running) { setAiFill(res.data); pollAiFill(); } })
            .catch(() => {});
    }, [token]);

    const [aiStarting, setAiStarting] = useState(false);

    const startAiFill = async (mode = 'missing') => {
        if (aiStarting || aiFill?.running) return;
        setAiStarting(true);
        try {
            const res = await axios.post(`${API}/admin/ai-fill-drafts`, { mode }, {
                headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
            });
            toast.success(res.data.message);
            setAiFill({ running: true, total: res.data.total, done: 0, errors: 0 });
            pollAiFill();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Грешка при стартиране на AI попълване');
        } finally {
            setAiStarting(false);
        }
    };

    const AI_MODE_LABELS = {
        fill_empty: 'Попълни само празните полета',
        title_only: 'Пренапиши само заглавието',
        description_only: 'Пренапиши само описанието',
        title_description: 'Пренапиши заглавие и описание',
    };

    const aiSuggestOne = async (product, mode = 'fill_empty', skipConfirm = false) => {
        if (aiLoadingId) {
            toast.warning('Изчакайте текущата AI обработка да приключи');
            return;
        }
        // Rewrite modes overwrite manual data -> require explicit confirmation
        if (!skipConfirm && mode !== 'fill_empty') {
            const targets = mode === 'title_only' ? ['заглавието'] : mode === 'description_only' ? ['описанието'] : ['заглавието', 'описанието'];
            const hasExisting = (mode !== 'description_only' && product.name && !product.name.toLowerCase().startsWith('draft ')) ||
                (mode !== 'title_only' && product.description);
            if (hasExisting && !window.confirm(`AI ще ПРЕЗАПИШЕ ${targets.join(' и ')} на „${product.name || 'продукта'}“. Продължавате ли?`)) return;
        }
        setAiLoadingId(product.product_id);
        try {
            const res = await axios.post(`${API}/admin/products/${product.product_id}/ai-suggest`, { mode }, {
                headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` },
                timeout: 300000,
            });
            setProducts(prev => prev.map(p => p.product_id === product.product_id ? { ...p, ...res.data.fields } : p));
            if (res.data.needs_review) {
                toast.warning(`🔍 ${res.data.message} — маркиран за ръчна проверка`);
            } else {
                toast.success(`✨ ${res.data.message}`);
            }
            if (res.data.skipped_fields?.length) {
                toast.info(`Запазени ръчни данни: ${res.data.skipped_fields.join(', ')}`);
            }
            fetchDuplicatesCount();
            return res.data;
        } catch (error) {
            const msg = error.response?.data?.detail || 'AI грешка при разпознаване';
            toast.error(msg, {
                action: { label: 'Опитай отново', onClick: () => setTimeout(() => aiSuggestOne(product, mode, true), 100) },
                duration: 8000,
            });
        } finally {
            setAiLoadingId(null);
        }
    };

    // AI action from the edit form: run + apply resulting fields into the open form
    const aiSuggestForForm = async (mode) => {
        if (!editingProduct) return;
        const res = await aiSuggestOne(editingProduct, mode);
        if (!res?.fields) return;
        setFormData(prev => {
            const next = { ...prev };
            Object.entries(res.fields).forEach(([k, v]) => {
                if (!(k in next)) return;
                const formatted = k === 'tags'
                    ? (Array.isArray(v) ? v.join(', ') : (v || ''))
                    : (v == null ? '' : (typeof v === 'number' ? String(v) : v));
                if (mode === 'fill_empty' && next[k] !== '' && next[k] != null && k !== 'weight_unit') return; // don't clobber unsaved manual input
                next[k] = formatted;
            });
            return next;
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        try {
            const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            // Response now contains base64 data URL directly
            setFormData(prev => ({ ...prev, image_url: response.data.url }));
            toast.success('Снимката е качена успешно');
        } catch (error) {
            toast.error('Грешка при качване на снимката');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e, saveAsDraft = false) => {
        e?.preventDefault();
        // Save scroll position before update
        scrollPositionRef.current = window.scrollY;
        
        // Validate required fields if not saving as draft
        if (!saveAsDraft) {
            const missingFields = [];
            if (!formData.name || formData.name.trim() === '') missingFields.push('Име');
            if (!formData.price || parseFloat(formData.price) <= 0) missingFields.push('Цена');
            if (!formData.category_id) missingFields.push('Категория');
            
            if (missingFields.length > 0) {
                toast.error(`Липсват задължителни полета: ${missingFields.join(', ')}`);
                return;
            }
            
            const reqCat = categories.find(c => c.category_id === formData.category_id);
            if (reqCat?.require_brand && !formData.brand_id) {
                if (!window.confirm(`Категорията "${reqCat.name}" изисква марка за всеки продукт. Продуктът ще бъде отбелязан в "Липсваща марка". Продължавате ли без марка?`)) return;
            }
        }
        
        try {
            const data = {
                ...formData,
                price: formData.price ? parseFloat(formData.price) : null,
                promotional_price: formData.promotional_price ? parseFloat(formData.promotional_price) : null,
                stock: formData.stock ? parseInt(formData.stock) : 0,
                discount_percent: formData.discount_percent ? parseFloat(formData.discount_percent) : null,
                weight: formData.weight ? parseFloat(formData.weight) : null,
                tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
                needs_review: false,
                variations: [],
                is_draft: saveAsDraft
            };

            if (editingProduct) {
                await axios.put(`${API}/admin/products/${editingProduct.product_id}`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Update product in local state instead of refetching all
                setProducts(prev => prev.map(p => 
                    p.product_id === editingProduct.product_id ? { ...p, ...data } : p
                ));
                toast.success(saveAsDraft ? 'Черновата е запазена' : 'Продуктът е обновен');
            } else {
                const response = await axios.post(`${API}/admin/products`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Add new product to local state
                setProducts(prev => [response.data, ...prev]);
                toast.success(saveAsDraft ? 'Черновата е запазена' : 'Продуктът е създаден');
            }
            setShowDialog(false);
            resetForm();
            
            // Restore scroll position
            setTimeout(() => {
                window.scrollTo(0, scrollPositionRef.current);
            }, 100);
        } catch (error) {
            console.error('Error:', error);
            toast.error(formatApiError(error, 'Грешка при запазване на продукта.'));
        }
    };

    // Check if product has all required fields and return missing fields
    const getProductIssues = (product) => {
        const issues = [];
        if (!product.name || product.name.trim() === '') issues.push('Име');
        if (!product.price || product.price <= 0) issues.push('Цена');
        if (!product.category_id) issues.push('Категория');
        if (!product.description || product.description.trim() === '') issues.push('Описание');
        // Admin list endpoint returns has_image flag; fall back to legacy fields for edit form
        const hasImage = product.has_image === true
            || Boolean(product.image_url)
            || Boolean(product.base64_image);
        if (!hasImage) issues.push('Снимка');
        if (product.stock === undefined || product.stock === null || product.stock < 0) issues.push('Наличност');
        const prodCat = categories.find(c => c.category_id === product.category_id);
        if (prodCat?.require_brand && !product.brand_id) issues.push('Марка');
        return issues;
    };

    const isProductComplete = (product) => {
        return getProductIssues(product).length === 0 && !product.is_draft;
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('Сигурни ли сте?')) return;
        try {
            await axios.delete(`${API}/admin/products/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Remove from local state instead of refetching
            setProducts(prev => prev.filter(p => p.product_id !== productId));
            toast.success('Продуктът е изтрит');
        } catch (error) {
            toast.error('Грешка при изтриване');
        }
    };

    const toggleVisibility = async (product) => {
        try {
            await axios.put(`${API}/admin/products/${product.product_id}/visibility`, 
                { is_visible: !product.is_visible },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Update local state
            setProducts(prev => prev.map(p => 
                p.product_id === product.product_id ? { ...p, is_visible: !p.is_visible } : p
            ));
            toast.success(product.is_visible ? 'Продуктът е скрит' : 'Продуктът е видим');
        } catch (error) {
            toast.error('Грешка');
        }
    };

    const toggleFlag = async (product, flag) => {
        try {
            await axios.put(`${API}/admin/products/${product.product_id}/flags`, 
                { [flag]: !product[flag] },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Update local state
            setProducts(prev => prev.map(p => 
                p.product_id === product.product_id ? { ...p, [flag]: !p[flag] } : p
            ));
            toast.success('Обновено');
        } catch (error) {
            toast.error('Грешка');
        }
    };

    // Quick category change for single product
    const quickChangeCategory = async (productId, newCategoryId, newSubcategory = '') => {
        try {
            await axios.put(`${API}/admin/products/${productId}/category`, 
                { category_id: newCategoryId, subcategory: newSubcategory },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Update local state
            setProducts(prev => prev.map(p => 
                p.product_id === productId ? { ...p, category_id: newCategoryId, subcategory: newSubcategory } : p
            ));
            toast.success('Категорията е променена');
        } catch (error) {
            console.error('Error:', error);
            toast.error('Грешка при промяна на категорията');
        }
    };

    // Bulk move products to category
    const bulkMoveProducts = async (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        
        if (!targetCategory || selectedProducts.length === 0) {
            toast.error('Изберете продукти и категория');
            return;
        }
        
        try {
            // Moving products to new category
            const promises = selectedProducts.map(productId => 
                axios.put(`${API}/admin/products/${productId}/category`, 
                    { category_id: targetCategory, subcategory: targetSubcategory || '' },
                    { headers: { Authorization: `Bearer ${token}` } }
                )
            );
            await Promise.all(promises);
            // Update local state
            setProducts(prev => prev.map(p => 
                selectedProducts.includes(p.product_id) 
                    ? { ...p, category_id: targetCategory, subcategory: targetSubcategory || '' } 
                    : p
            ));
            toast.success(`${selectedProducts.length} продукта са преместени`);
            setSelectedProducts([]);
            setShowMoveDialog(false);
            setTargetCategory('');
            setTargetSubcategory('');
        } catch (error) {
            console.error('Error moving products:', error);
            toast.error('Грешка при преместване: ' + (error.response?.data?.detail || error.message));
        }
    };

    // Toggle product selection (with 100-max limit)
    const MAX_BULK_SELECT = 100;
    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            }
            if (prev.length >= MAX_BULK_SELECT) {
                toast.error(`Максимум ${MAX_BULK_SELECT} продукта на една групова операция. Премахнете някой за да добавите нов.`);
                return prev;
            }
            return [...prev, productId];
        });
    };

    // Select all visible products (only current page in list view) — capped at 100
    const selectAllProducts = () => {
        const productsToSelect = productsViewMode === 'list' 
            ? paginatedProducts.map(p => p.product_id)
            : filteredProducts.map(p => p.product_id);

        if (selectedProducts.length === productsToSelect.length && productsToSelect.every(id => selectedProducts.includes(id))) {
            setSelectedProducts([]);
            return;
        }
        if (productsToSelect.length > MAX_BULK_SELECT) {
            toast.warning(`Списъкът има ${productsToSelect.length} продукта. Избирам първите ${MAX_BULK_SELECT} (максимум за групова операция).`);
        }
        setSelectedProducts(productsToSelect.slice(0, MAX_BULK_SELECT));
    };

    // Select up to 100 from full filtered list (regardless of pagination)
    const selectUpTo100Filtered = () => {
        const ids = filteredProducts.map(p => p.product_id).slice(0, MAX_BULK_SELECT);
        setSelectedProducts(ids);
        if (filteredProducts.length > MAX_BULK_SELECT) {
            toast.info(`Избрани първите ${MAX_BULK_SELECT} от ${filteredProducts.length} филтрирани.`);
        } else {
            toast.success(`Избрани всички ${ids.length} филтрирани продукта.`);
        }
    };

    // ========== NEW FUNCTIONS ==========
    
    // 1. Inline Edit - Save single field
    const saveInlineEdit = async () => {
        if (!inlineEdit.productId || !inlineEdit.field) return;
        
        try {
            const updateData = {};
            if (inlineEdit.field === 'price' || inlineEdit.field === 'stock') {
                updateData[inlineEdit.field] = parseFloat(inlineEdit.value) || 0;
            } else {
                updateData[inlineEdit.field] = inlineEdit.value;
            }
            
            // Use PATCH for partial updates to preserve other fields like image_url
            await axios.patch(`${API}/admin/products/${inlineEdit.productId}`, updateData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Update local state for instant feedback
            setProducts(prev => prev.map(p => 
                p.product_id === inlineEdit.productId 
                    ? { ...p, ...updateData }
                    : p
            ));
            toast.success('Запазено');
            setInlineEdit({ productId: null, field: null, value: '' });
        } catch (error) {
            toast.error('Грешка при запазване');
        }
    };

    // 2. Duplicate Product
    const duplicateProduct = async (product) => {
        try {
            const newProduct = {
                ...product,
                name: `${product.name} (копие)`,
                is_draft: true,
                image_url: '', // Clear image so user can upload new one
                images: []
            };
            delete newProduct.product_id;
            delete newProduct.created_at;
            
            const response = await axios.post(`${API}/admin/products`, newProduct, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Продуктът е дублиран - редактирайте го');
            
            // Open edit dialog immediately with the new product data
            const createdProduct = response.data;
            if (createdProduct && createdProduct.product_id) {
                // Set editing product first, then fetch to update list in background
                setEditingProduct(createdProduct);
                setFormData({
                    name: createdProduct.name || '',
                    description: createdProduct.description || '',
                    price: createdProduct.price?.toString() || '',
                    promotional_price: createdProduct.promotional_price?.toString() || '',
                    category_id: createdProduct.category_id || '',
                    category_ids: createdProduct.category_ids || [],
                    subcategory: createdProduct.subcategory || '',
                    brand: createdProduct.brand || '',
                    brand_id: createdProduct.brand_id || null,
                    image_url: createdProduct.image_url || '',
                    stock: createdProduct.stock?.toString() || '0',
                    is_featured: createdProduct.is_featured || false,
                    is_new: createdProduct.is_new || false,
                    is_bestseller: createdProduct.is_bestseller || false,
                    is_visible: createdProduct.is_visible !== false,
                    is_draft: createdProduct.is_draft || false,
                    discount_percent: createdProduct.discount_percent?.toString() || '',
                    requires_prepayment_outside_sofia: createdProduct.requires_prepayment_outside_sofia === true
                });
                setShowDialog(true);
                
                // Fetch data in background to update the list
                fetchData();
            }
        } catch (error) {
            toast.error('Грешка при дублиране');
        }
    };

    // Bulk duplicate selected products
    const bulkDuplicateProducts = async () => {
        if (selectedProducts.length === 0) {
            toast.error('Изберете продукти за дублиране');
            return;
        }
        
        try {
            const newProducts = [];
            for (const productId of selectedProducts) {
                const product = products.find(p => p.product_id === productId);
                if (!product) continue;
                
                const newProduct = {
                    ...product,
                    name: `${product.name} (копие)`,
                    is_draft: true,
                    image_url: '', // Clear image
                    thumbnail_url: '',
                    images: []
                };
                delete newProduct.product_id;
                delete newProduct.created_at;
                
                const response = await axios.post(`${API}/admin/products`, newProduct, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                newProducts.push(response.data);
            }
            
            // Add new products to local state
            setProducts(prev => [...newProducts, ...prev]);
            toast.success(`${newProducts.length} продукта са дублирани като чернови`);
            setSelectedProducts([]);
        } catch (error) {
            toast.error('Грешка при масово дублиране');
        }
    };

    // 4. Apply Template
    const applyTemplate = (template) => {
        setFormData(prev => ({
            ...prev,
            category_id: template.category_id,
            description: template.description
        }));
        toast.success(`Шаблон "${template.name}" е приложен`);
    };

    // 6. Bulk Price Change
    const applyBulkPriceChange = async () => {
        if (selectedProducts.length === 0 || !bulkPriceValue) {
            toast.error('Изберете продукти и въведете стойност');
            return;
        }
        
        try {
            const value = parseFloat(bulkPriceValue);
            const promises = selectedProducts.map(async (productId) => {
                const product = products.find(p => p.product_id === productId);
                if (!product || !product.price) return;
                
                let newPrice;
                if (bulkPriceAction === 'percent') {
                    newPrice = product.price * (1 + value / 100);
                } else {
                    newPrice = product.price + value;
                }
                newPrice = Math.max(0, Math.round(newPrice * 100) / 100);
                
                // Use PATCH for partial updates to preserve image_url
                return axios.patch(`${API}/admin/products/${productId}`, 
                    { price: newPrice },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            });
            
            await Promise.all(promises);
            
            // Update local state
            const priceValue = parseFloat(bulkPriceValue);
            setProducts(prev => prev.map(p => {
                if (!selectedProducts.includes(p.product_id) || !p.price) return p;
                let updatedPrice;
                if (bulkPriceAction === 'percent') {
                    updatedPrice = p.price * (1 + priceValue / 100);
                } else {
                    updatedPrice = p.price + priceValue;
                }
                return { ...p, price: Math.max(0, Math.round(updatedPrice * 100) / 100) };
            }));
            
            toast.success(`Цените на ${selectedProducts.length} продукта са обновени`);
            setShowBulkPriceDialog(false);
            setBulkPriceValue('');
            setSelectedProducts([]);
        } catch (error) {
            toast.error('Грешка при промяна на цените');
        }
    };

    // ========== END NEW FUNCTIONS ==========

    // NEW: Duplicates count (badge on toolbar button)
    const fetchDuplicatesCount = async () => {
        try {
            const t = token || localStorage.getItem('token');
            const res = await axios.get(`${API}/admin/duplicates/count`, { headers: { Authorization: `Bearer ${t}` } });
            setDuplicatesCount(res.data.pending || 0);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { fetchDuplicatesCount(); }, [token]); // eslint-disable-line

    // ===== NEW: Bulk operations on selected products =====

    // Start AI fill on selected drafts
    const startAiFillSelected = async () => {
        if (selectedProducts.length === 0) {
            toast.error('Няма избрани продукти');
            return;
        }
        if (aiFill?.running) {
            toast.error('AI попълването вече е в процес');
            return;
        }
        setAiSelectedStarting(true);
        try {
            const res = await axios.post(
                `${API}/admin/ai-fill-drafts`,
                { product_ids: selectedProducts, overwrite: aiSelectedOverwrite },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
            setAiFill({ running: true, total: res.data.total, done: 0, errors: 0, products: {} });
            pollAiFill();
            setShowAiSelectedDialog(false);
            setShowAiProgressPanel(true);
            setAiProgressErrorsOnly(false);
        } catch (error) {
            toast.error('Грешка: ' + (error.response?.data?.detail || error.message));
        } finally {
            setAiSelectedStarting(false);
        }
    };

    // Request stop
    const stopAiFill = async () => {
        setAiStopping(true);
        try {
            const res = await axios.post(
                `${API}/admin/ai-fill-drafts/stop`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.info(res.data.message);
        } catch (error) {
            toast.error('Грешка при спиране: ' + (error.response?.data?.detail || error.message));
        } finally {
            setAiStopping(false);
        }
    };

    // Generic bulk POST helper
    const doBulkAction = async (endpoint, extraPayload = {}, successMsg = null) => {
        if (selectedProducts.length === 0) {
            toast.error('Няма избрани продукти');
            return;
        }
        try {
            const res = await axios.post(
                `${API}${endpoint}`,
                { product_ids: selectedProducts, ...extraPayload },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(successMsg || res.data.message);
            await fetchData();
            setSelectedProducts([]);
        } catch (error) {
            toast.error('Грешка: ' + (error.response?.data?.detail || error.message));
        }
    };

    const bulkPublishSelected = () => doBulkAction('/admin/products/bulk-publish');
    const bulkDraftSelected = () => doBulkAction('/admin/products/bulk-draft');
    const bulkShowSelected = () => doBulkAction('/admin/products/bulk-visibility', { is_visible: true });
    const bulkHideSelected = () => doBulkAction('/admin/products/bulk-visibility', { is_visible: false });

    const bulkDeleteSelected = async () => {
        setBulkDeleting(true);
        try {
            const res = await axios.post(
                `${API}/admin/products/bulk-delete`,
                { product_ids: selectedProducts },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
            await fetchData();
            setSelectedProducts([]);
            setShowBulkDeleteDialog(false);
        } catch (error) {
            toast.error('Грешка: ' + (error.response?.data?.detail || error.message));
        } finally {
            setBulkDeleting(false);
        }
    };

    const bulkSetBrand = async () => {
        setBulkBrandSaving(true);
        try {
            const res = await axios.post(
                `${API}/admin/products/bulk-brand`,
                { product_ids: selectedProducts, brand_id: bulkBrandValue === '__remove__' ? null : bulkBrandValue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
            await fetchData();
            setSelectedProducts([]);
            setShowBulkBrandDialog(false);
            setBulkBrandValue('');
        } catch (error) {
            toast.error('Грешка: ' + (error.response?.data?.detail || error.message));
        } finally {
            setBulkBrandSaving(false);
        }
    };

    const runAutoAssignBrands = async () => {
        setAutoAssignRunning(true);
        try {
            const payload = filterCategory !== 'all' ? { category_id: filterCategory } : {};
            const res = await axios.post(
                `${API}/admin/brands/auto-import`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAutoAssignReport(res.data);
            toast.success(res.data.message);
            await fetchData();
        } catch (error) {
            toast.error('Грешка: ' + (error.response?.data?.detail || error.message));
        } finally {
            setAutoAssignRunning(false);
        }
    };

    const bulkSetStock = async () => {
        const val = parseInt(bulkStockValue, 10);
        if (isNaN(val) || val < 0) {
            toast.error('Въведете валидна наличност (число ≥ 0)');
            return;
        }
        setBulkStockSaving(true);
        try {
            const res = await axios.post(
                `${API}/admin/products/bulk-stock`,
                { product_ids: selectedProducts, stock: val },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
            await fetchData();
            setSelectedProducts([]);
            setShowBulkStockDialog(false);
            setBulkStockValue('');
        } catch (error) {
            toast.error('Грешка: ' + (error.response?.data?.detail || error.message));
        } finally {
            setBulkStockSaving(false);
        }
    };

    // Filter products by category (check both category_id and category_ids)
    const filteredProducts = (filterCategory === 'all' 
        ? products 
        : products.filter(p => 
            p.category_id === filterCategory || 
            (p.category_ids && p.category_ids.includes(filterCategory))
        )).filter(p => !filterNoBrand || !p.brand_id)
        .filter(p => filterBrand === 'all' || (filterBrand === 'none' ? !p.brand_id : p.brand_id === filterBrand))
        .filter(p => !filterNoBarcode || (!p.barcode && !p.sku))
        .filter(p => {
            const q = productSearch.trim().toLowerCase();
            if (!q) return true;
            return [p.name, p.barcode, p.sku].some(v => (v || '').toLowerCase().includes(q));
        });

    const noBarcodeCount = useMemo(() => products.filter(p => !p.barcode && !p.sku).length, [products]);

    // Group products by category and subcategory for folder view
    const groupedProducts = useMemo(() => {
        const groups = {};
        
        // First, add all categories with empty arrays and pre-populate
        // subcategories from the category's own subcategories[] field so that
        // subcategories that were created but don't have products yet still show.
        categories.forEach(cat => {
            const preSubs = {};
            (cat.subcategories || []).forEach(s => {
                const name = (s || '').trim();
                if (name) preSubs[name] = [];
            });
            groups[cat.category_id] = {
                category: cat,
                subcategories: preSubs,
                uncategorized: []
            };
        });
        
        // Add "Без категория" group
        groups['uncategorized'] = {
            category: { category_id: 'uncategorized', name: 'Без категория' },
            subcategories: {},
            uncategorized: []
        };
        
        // Group products
        filteredProducts.forEach(product => {
            const catId = product.category_id || 'uncategorized';
            const subcat = product.subcategory || '';
            
            if (!groups[catId]) {
                groups[catId] = {
                    category: { category_id: catId, name: catId },
                    subcategories: {},
                    uncategorized: []
                };
            }
            
            if (subcat) {
                if (!groups[catId].subcategories[subcat]) {
                    groups[catId].subcategories[subcat] = [];
                }
                groups[catId].subcategories[subcat].push(product);
            } else {
                groups[catId].uncategorized.push(product);
            }
        });
        
        // Remove categories that have no products AND no configured subcategories
        Object.keys(groups).forEach(key => {
            const group = groups[key];
            const hasProducts = group.uncategorized.length > 0 || 
                Object.values(group.subcategories).some(arr => arr.length > 0);
            const hasConfiguredSubs = Object.keys(group.subcategories).length > 0;
            if (!hasProducts && !hasConfiguredSubs) {
                delete groups[key];
            }
        });
        
        return groups;
    }, [filteredProducts, categories]);

    // Group products inside a folder by brand (Category → Subcategory → Brand → Products)
    const renderBrandGrouped = (items, keyPrefix, renderRow) => {
        if (!items.some(p => p.brand_id)) return items.map(renderRow);
        const brandGroups = {};
        items.forEach(p => {
            const key = p.brand_id || '__none__';
            (brandGroups[key] = brandGroups[key] || []).push(p);
        });
        const entries = Object.entries(brandGroups).sort((a, b) => {
            if (a[0] === '__none__') return 1;
            if (b[0] === '__none__') return -1;
            const an = brands.find(x => x.brand_id === a[0])?.name || '';
            const bn = brands.find(x => x.brand_id === b[0])?.name || '';
            return an.localeCompare(bn);
        });
        return entries.map(([bid, prods]) => {
            const bKey = `${keyPrefix}::${bid}`;
            const isOpen = expandedBrandFolders[bKey];
            const brand = brands.find(x => x.brand_id === bid);
            const noBrand = bid === '__none__';
            const label = noBrand ? 'Без зададена марка' : (brand?.name || prods[0]?.brand || bid);
            return (
                <div key={bKey} className="border-l-2 border-purple-200 dark:border-purple-800 ml-1">
                    <button
                        onClick={() => setExpandedBrandFolders(prev => ({ ...prev, [bKey]: !prev[bKey] }))}
                        className="w-full flex items-center gap-2 p-1.5 pl-3 hover:bg-muted/30 dark:hover:bg-slate-700/30 text-left"
                        data-testid={`brand-folder-${bid}`}
                    >
                        {isOpen ? <FolderOpen className="h-3.5 w-3.5 text-purple-400" /> : <Folder className="h-3.5 w-3.5 text-purple-400" />}
                        {brand?.logo_url && <img src={brand.logo_url} alt="" className="w-4 h-4 rounded-full object-cover" />}
                        <span className={`text-xs font-medium ${noBrand ? 'text-amber-600 dark:text-amber-400' : 'dark:text-slate-300'}`}>{label}</span>
                        <Badge variant="outline" className="ml-auto text-[10px]">{prods.length}</Badge>
                        {isOpen ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                    </button>
                    {isOpen && <div className="ml-3 pl-2 space-y-1">{prods.map(renderRow)}</div>}
                </div>
            );
        });
    };

    // Toggle category expansion
    const toggleCategory = (catId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [catId]: !prev[catId]
        }));
    };

    // Toggle subcategory expansion
    const toggleSubcategory = (catId, subcat) => {
        const key = `${catId}-${subcat}`;
        setExpandedSubcategories(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Expand all categories
    const expandAll = () => {
        const expanded = {};
        Object.keys(groupedProducts).forEach(catId => {
            expanded[catId] = true;
        });
        setExpandedCategories(expanded);
        
        const expandedSubs = {};
        Object.entries(groupedProducts).forEach(([catId, group]) => {
            Object.keys(group.subcategories).forEach(subcat => {
                expandedSubs[`${catId}-${subcat}`] = true;
            });
        });
        setExpandedSubcategories(expandedSubs);
    };

    // Collapse all categories
    const collapseAll = () => {
        setExpandedCategories({});
        setExpandedSubcategories({});
    };

    // Paginated products for list view
    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * productsPerPage;
        return filteredProducts.slice(startIndex, startIndex + productsPerPage);
    }, [filteredProducts, currentPage, productsPerPage]);

    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filterCategory]);

    const targetCategoryData = categories.find(c => c.category_id === targetCategory);

    const openEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name || '',
            description: product.description || '',
            price: product.price?.toString() || '',
            promotional_price: product.promotional_price?.toString() || '',
            category_id: product.category_id || '',
            category_ids: product.category_ids || [],
            category_subcategories: product.category_subcategories || {},
            subcategory: product.subcategory || '',
            sub_subcategory: product.sub_subcategory || '',
            brand: product.brand || '',
            brand_id: product.brand_id || null,
            image_url: product.image_url || '',
            stock: product.stock?.toString() || '0',
            is_featured: product.is_featured || false,
            is_new: product.is_new || false,
            is_bestseller: product.is_bestseller || false,
            is_visible: product.is_visible !== false,
            is_draft: product.is_draft || false,
            discount_percent: product.discount_percent?.toString() || '',
            weight: product.weight?.toString() || '',
            weight_unit: product.weight_unit || 'гр',
            weight_visible: product.weight_visible === true,
            requires_prepayment_outside_sofia: product.requires_prepayment_outside_sofia === true,
            original_name: product.original_name || '',
            short_description: product.short_description || '',
            composition: product.composition || '',
            allergens: product.allergens || '',
            nutrition: product.nutrition || '',
            usage_instructions: product.usage_instructions || '',
            warnings: product.warnings || '',
            origin_country: product.origin_country || '',
            barcode: product.barcode || '',
            sku: product.sku || '',
            tags: Array.isArray(product.tags) ? product.tags.join(', ') : '',
            seo_title: product.seo_title || '',
            seo_description: product.seo_description || '',
            slug: product.slug || ''
        });
        setShowExtraFields(Boolean(product.composition || product.barcode || product.sku || product.seo_title || product.origin_country));
        setShowDialog(true);
    };

    const resetForm = () => {
        setEditingProduct(null);
        setShowExtraFields(false);
        setFormData({
            name: '', description: '', price: '', promotional_price: '', 
            category_id: '', category_ids: [], category_subcategories: {}, subcategory: '', sub_subcategory: '',
            brand: '', brand_id: null, image_url: '', stock: '', is_featured: false, is_new: false,
            is_bestseller: false, is_visible: true, is_draft: false, discount_percent: '',
            weight: '', weight_unit: 'гр', weight_visible: false,
            requires_prepayment_outside_sofia: false,
            ...EXTRA_FIELDS_DEFAULTS
        });
    };

    // Toggle category selection for multi-select
    const toggleCategorySelection = (categoryId) => {
        setFormData(prev => {
            const selected = prev.category_ids.includes(categoryId);
            const nextSubs = { ...prev.category_subcategories };
            if (selected) delete nextSubs[categoryId];
            return {
                ...prev,
                category_ids: selected
                    ? prev.category_ids.filter(id => id !== categoryId)
                    : [...prev.category_ids, categoryId],
                category_subcategories: nextSubs
            };
        });
    };

    const setAdditionalSubcategory = (categoryId, sub) => {
        setFormData(prev => {
            const nextSubs = { ...prev.category_subcategories };
            if (!sub || sub === '__none__') delete nextSubs[categoryId];
            else nextSubs[categoryId] = sub;
            return { ...prev, category_subcategories: nextSubs };
        });
    };

    const selectedCategory = categories.find(c => c.category_id === formData.category_id);

    return (
        <div>
            {showOrderManager && (
                <ProductOrderManager categories={categories} token={token} onClose={() => setShowOrderManager(false)} />
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <h1 className="font-serif text-3xl font-bold dark:text-white">Продукти</h1>
                    <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => {
                            setLoading(true);
                            fetchData().then(() => {
                                toast.success('Продуктите са обновени');
                            });
                        }}
                        className="dark:border-slate-600 dark:hover:bg-slate-700"
                        title="Обнови списъка"
                    >
                        <RefreshCw className={`h-4 w-4 dark:text-slate-300 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <span className="text-sm text-muted-foreground dark:text-slate-400" data-testid="products-total-count">
                        {products.length} продукта
                    </span>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <Input
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Търси по име, баркод, SKU..."
                        className="w-56 h-9"
                        data-testid="admin-product-search"
                    />
                    <Button
                        size="sm"
                        variant={filterNoBarcode ? 'default' : 'outline'}
                        onClick={() => setFilterNoBarcode(v => !v)}
                        title="Покажи само продукти без баркод/SKU (за напасване с iCash)"
                        data-testid="filter-no-barcode-btn"
                        className={filterNoBarcode ? 'bg-amber-500 hover:bg-amber-600' : ''}
                    >
                        Без баркод ({noBarcodeCount})
                    </Button>
                    {/* View Mode Toggle */}
                    <div className="flex border rounded-lg overflow-hidden dark:border-slate-600">
                        <Button 
                            variant={productsViewMode === 'folders' ? "default" : "ghost"} 
                            size="sm"
                            onClick={() => setProductsViewMode('folders')}
                            className={`rounded-none ${productsViewMode === 'folders' ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                        >
                            <FolderTree className="h-4 w-4 mr-1" />
                            Папки
                        </Button>
                        <Button 
                            variant={productsViewMode === 'list' ? "default" : "ghost"} 
                            size="sm"
                            onClick={() => setProductsViewMode('list')}
                            className={`rounded-none ${productsViewMode === 'list' ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                        >
                            <List className="h-4 w-4 mr-1" />
                            Списък
                        </Button>
                    </div>
                    
                    {/* AI Fill Drafts */}
                    <Button
                        variant="outline"
                        onClick={() => setShowOrderManager(true)}
                        data-testid="manual-order-btn"
                        className="dark:border-slate-600 border-amber-500 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                        title="Ръчно подреждане на продуктите в магазина"
                    >
                        <ArrowRightLeft className="h-4 w-4 mr-2 rotate-90" />
                        Ръчно подреждане
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => { startAiFill('missing'); setShowAiProgressPanel(true); }}
                        disabled={aiFill?.running || aiStarting}
                        data-testid="ai-fill-drafts-btn"
                        className="dark:border-slate-600 border-purple-500 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20"
                    >
                        <Sparkles className={`h-4 w-4 mr-2 ${aiFill?.running ? 'animate-pulse' : ''}`} />
                        {aiFill?.running ? `AI попълва... ${aiFill.done}/${aiFill.total}` : 'AI попълни чернови'}
                    </Button>

                    {/* Bulk Import Images -> Drafts */}
                    <Button
                        variant="outline"
                        onClick={() => setShowBulkImportDialog(true)}
                        data-testid="bulk-import-drafts-btn"
                        className="dark:border-slate-600 border-cyan-500 text-cyan-600 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:bg-cyan-900/20"
                        title="Качи до 100 снимки наведнъж като чернови"
                    >
                        <ImagePlus className="h-4 w-4 mr-2" />
                        Bulk Import Снимки
                    </Button>

                    {/* Possible Duplicates */}
                    <Button
                        variant="outline"
                        onClick={() => setShowDuplicates(true)}
                        data-testid="duplicates-open-btn"
                        className="dark:border-slate-600 border-orange-500 text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20 relative"
                        title="Продукти, разпознати като възможни дубликати"
                    >
                        <Copy className="h-4 w-4 mr-2" />
                        Възможни дубликати
                        {duplicatesCount > 0 && (
                            <span className="ml-2 bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5" data-testid="duplicates-count-badge">
                                {duplicatesCount}
                            </span>
                        )}
                    </Button>

                    {/* AI Restyle Titles + Descriptions */}
                    <Button
                        variant="outline"
                        onClick={() => {
                            if (window.confirm('AI ще пренапише ЗАГЛАВИЯТА и ОПИСАНИЯТА на черновите (в стила на магазина с ✨ 💪 ✔️) и ще избере категория за тези без категория. Ръчно попълнени категории, цени и марки НЕ се променят. Продължавате?')) {
                                startAiFill('restyle');
                                setShowAiProgressPanel(true);
                            }
                        }}
                        disabled={aiFill?.running || aiStarting}
                        data-testid="ai-restyle-drafts-btn"
                        className="dark:border-slate-600 border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50 dark:text-fuchsia-400 dark:hover:bg-fuchsia-900/20"
                        title="Пренаписва заглавията и описанията на черновите и избира категории"
                    >
                        <Sparkles className={`h-4 w-4 mr-2 ${aiFill?.running ? 'animate-pulse' : ''}`} />
                        AI заглавия + описания
                    </Button>
                    
                    {/* Quick Mode Toggle */}
                    <Button 
                        variant={quickMode ? "default" : "outline"} 
                        onClick={() => setQuickMode(!quickMode)}
                        className={quickMode ? "bg-green-500 hover:bg-green-600" : "dark:border-slate-600 dark:text-slate-300"}
                    >
                        <Zap className="h-4 w-4 mr-2" />
                        {quickMode ? "Бърз режим ВКЛ" : "Бърз режим"}
                    </Button>
                    
                    {/* Activate All Products Button */}
                    <Button 
                        type="button"
                        variant="outline"
                        onClick={async () => {
                            try {
                                const response = await axios.post(`${API}/admin/activate-all-products`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success(response.data.message);
                                fetchData(); // Refresh products
                            } catch (error) {
                                toast.error('Грешка при активиране: ' + (error.response?.data?.detail || error.message));
                            }
                        }}
                        className="dark:border-slate-600 dark:text-slate-300 border-green-500 text-green-600 hover:bg-green-50"
                    >
                        <Check className="h-4 w-4 mr-2" />
                        Активирай всички
                    </Button>
                    
                    <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary" data-testid="add-product-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Добави продукт
                            </Button>
                        </DialogTrigger>
                        <DialogContent onCloseAutoFocus={(e) => e.preventDefault()} className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
                            <DialogHeader>
                                <DialogTitle className="dark:text-white">{editingProduct ? 'Редактирай продукт' : 'Нов продукт'}</DialogTitle>
                            </DialogHeader>
                            
                            {/* Templates - only show for new products */}
                            {!editingProduct && (
                                <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <Label className="text-xs text-muted-foreground dark:text-slate-400 mb-2 block">⚡ Бързи шаблони</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {productTemplates.map(template => (
                                            <button
                                                key={template.id}
                                                type="button"
                                                onClick={() => applyTemplate(template)}
                                                className="px-3 py-1.5 text-sm bg-white dark:bg-slate-600 border dark:border-slate-500 rounded-lg hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-slate-500 transition-colors"
                                            >
                                                {template.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <Label>Име {!formData.is_draft && '*'}</Label>
                                        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="product-name-input" placeholder="Въведете име на продукта" />
                                    </div>
                                    <div>
                                        <Label>Цена (EUR) {!formData.is_draft && '*'}</Label>
                                        <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} data-testid="product-price-input" />
                                    </div>
                                    <div>
                                        <Label>Промоционална цена (EUR)</Label>
                                        <Input type="number" step="0.01" value={formData.promotional_price} onChange={(e) => setFormData({ ...formData, promotional_price: e.target.value })} placeholder="Оставете празно ако няма" data-testid="product-promo-price-input" />
                                    </div>
                                    <div>
                                        <Label>Наличност *</Label>
                                        <Input type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} data-testid="product-stock-input" />
                                    </div>
                                    <div>
                                        <Label>Категория *</Label>
                                        <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v, subcategory: '', sub_subcategory: '' })}>
                                            <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Избери основна" /></SelectTrigger>
                                            <SelectContent>
                                                {categories.filter(c => c.category_id).map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Подкатегория</Label>
                                        <Select value={formData.subcategory} onValueChange={(v) => setFormData({ ...formData, subcategory: v, sub_subcategory: '', requires_prepayment_outside_sofia: ['Месни продукти', 'Млечни продукти'].includes(v) ? true : formData.requires_prepayment_outside_sofia })}>
                                            <SelectTrigger data-testid="product-subcategory-select"><SelectValue placeholder="Избери" /></SelectTrigger>
                                            <SelectContent>
                                                {selectedCategory?.subcategories?.filter(s => s && s.trim()).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {formData.subcategory && (selectedCategory?.sub_subcategories?.[formData.subcategory]?.length > 0) && (
                                        <div className="col-span-2">
                                            <Label>Под-подкатегория (незадължително)</Label>
                                            <Select value={formData.sub_subcategory} onValueChange={(v) => setFormData({ ...formData, sub_subcategory: v === '__none__' ? '' : v })}>
                                                <SelectTrigger data-testid="product-subsub-select"><SelectValue placeholder="Избери под-подкатегория" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__none__">— Без под-подкатегория —</SelectItem>
                                                    {selectedCategory.sub_subcategories[formData.subcategory].map(ss => (
                                                        <SelectItem key={ss} value={ss}>{ss}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                
                                {/* Multiple Categories Selection */}
                                <div className="col-span-2">
                                    <Label className="mb-2 block">Допълнителни категории (показва се във всички избрани)</Label>
                                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 max-h-32 overflow-y-auto">
                                        {categories.map(c => (
                                            <button
                                                key={c.category_id}
                                                type="button"
                                                onClick={() => toggleCategorySelection(c.category_id)}
                                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                    formData.category_ids.includes(c.category_id)
                                                        ? 'bg-amber-500 text-white'
                                                        : 'bg-white dark:bg-slate-600 text-slate-700 dark:text-slate-300 border dark:border-slate-500 hover:border-amber-400'
                                                }`}
                                            >
                                                {formData.category_ids.includes(c.category_id) && <Check className="h-3 w-3 inline mr-1" />}
                                                {c.name}
                                            </button>
                                        ))}
                                    </div>
                                    {formData.category_ids.length > 0 && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                            Избрани: {formData.category_ids.length} допълнителни категории
                                        </p>
                                    )}

                                    {/* Per-additional-category subcategory choice (optional) */}
                                    {formData.category_ids.some(id => (categories.find(c => c.category_id === id)?.subcategories || []).length > 0) && (
                                        <div className="mt-3 space-y-2 p-3 border rounded-lg dark:border-slate-600" data-testid="additional-subcategories-section">
                                            <Label className="text-xs text-slate-500 dark:text-slate-400">
                                                Подкатегория за всяка допълнителна категория (по избор — може да остане само в категорията)
                                            </Label>
                                            {formData.category_ids.map(catId => {
                                                const cat = categories.find(c => c.category_id === catId);
                                                const subs = (cat?.subcategories || []).filter(s => s && s.trim());
                                                if (!cat || subs.length === 0) return null;
                                                return (
                                                    <div key={catId} className="flex items-center gap-2">
                                                        <span className="text-sm dark:text-white w-1/3 truncate" title={cat.name}>{cat.name}:</span>
                                                        <Select
                                                            value={formData.category_subcategories[catId] || '__none__'}
                                                            onValueChange={(v) => setAdditionalSubcategory(catId, v)}
                                                        >
                                                            <SelectTrigger className="flex-1 h-9 dark:bg-slate-700 dark:border-slate-600 dark:text-white" data-testid={`additional-subcategory-select-${catId}`}>
                                                                <SelectValue placeholder="Само в категорията" />
                                                            </SelectTrigger>
                                                            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                                                <SelectItem value="__none__">Само в категорията (без подкатегория)</SelectItem>
                                                                {subs.map(s => (
                                                                    <SelectItem key={s} value={s} className="dark:text-white dark:focus:bg-slate-700">{s}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                
                                <div>
                                    <Label>Марка</Label>
                                    {(() => {
                                        const currentInOptions = !formData.brand_id || brandOptions.some(b => b.brand_id === formData.brand_id);
                                        const currentBrand = brands.find(b => b.brand_id === formData.brand_id);
                                        return (
                                            <>
                                                <Select
                                                    value={formData.brand_id || '__none__'}
                                                    onValueChange={(v) => {
                                                        if (!v) return;
                                                        if (v === '__new__') { setShowNewBrand(true); return; }
                                                        const b = brandOptions.find(x => x.brand_id === v) || brands.find(x => x.brand_id === v);
                                                        setFormData(prev => ({ ...prev, brand_id: v === '__none__' ? null : v, brand: b ? b.name : '' }));
                                                    }}
                                                >
                                                    <SelectTrigger data-testid="product-brand-select" className={!currentInOptions ? 'border-amber-500' : ''}>
                                                        <SelectValue placeholder="Без марка" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="__none__">Без марка</SelectItem>
                                                        {!currentInOptions && currentBrand && (
                                                            <SelectItem value={currentBrand.brand_id} className="text-amber-600">
                                                                {currentBrand.name} (извън категорията)
                                                            </SelectItem>
                                                        )}
                                                        {brandOptions.map(b => (
                                                            <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>
                                                        ))}
                                                        <SelectItem value="__new__" className="text-amber-600 font-medium">+ Нова марка...</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                {!currentInOptions && currentBrand && (
                                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1" data-testid="brand-mismatch-warning">
                                                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                                        Марката „{currentBrand.name}“ не е свързана с избраната категория/подкатегория — изберете подходяща марка или я свържете от секция „Марки“.
                                                    </p>
                                                )}
                                                {brandsFiltered && (
                                                    <p className="text-[11px] text-muted-foreground mt-1" data-testid="brand-filter-hint">
                                                        Показани са само марките, свързани с избраната категория{formData.subcategory ? ' и подкатегория' : ''} ({brandOptions.length}).
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })()}
                                    {showNewBrand && (
                                        <div className="flex gap-2 mt-2">
                                            <Input
                                                value={newBrandName}
                                                onChange={(e) => setNewBrandName(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), createBrandInline())}
                                                placeholder="Име на новата марка"
                                                autoFocus
                                                data-testid="new-brand-name-input"
                                            />
                                            <Button type="button" onClick={createBrandInline} variant="outline" data-testid="create-brand-inline-btn">Създай</Button>
                                            <Button type="button" variant="ghost" size="icon" onClick={() => setShowNewBrand(false)}><X className="h-4 w-4" /></Button>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Weight/Volume Section */}
                                <div>
                                    <Label>Грамаж / Обем</Label>
                                    <div className="flex gap-2">
                                        <Input 
                                            type="number" 
                                            step="0.01"
                                            value={formData.weight} 
                                            onChange={(e) => setFormData({ ...formData, weight: e.target.value })} 
                                            placeholder="напр. 500"
                                            className="flex-1"
                                        />
                                        <Select value={formData.weight_unit} onValueChange={(v) => setFormData({ ...formData, weight_unit: v })}>
                                            <SelectTrigger className="w-24 dark:bg-slate-700 dark:border-slate-600">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="dark:bg-slate-800">
                                                <SelectItem value="гр">гр</SelectItem>
                                                <SelectItem value="кг">кг</SelectItem>
                                                <SelectItem value="мл">мл</SelectItem>
                                                <SelectItem value="л">л</SelectItem>
                                                <SelectItem value="бр">бр</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">Примери: 500 гр, 1 кг, 750 мл, 1 л</p>
                                    <label className="flex items-center gap-2 mt-2 cursor-pointer select-none" data-testid="weight-visible-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.weight_visible || false}
                                            onChange={(e) => setFormData({ ...formData, weight_visible: e.target.checked })}
                                            className="h-4 w-4 accent-amber-500"
                                            data-testid="weight-visible-checkbox"
                                        />
                                        <span className="text-sm">Покажи грамажа/обема на сайта</span>
                                    </label>
                                    <p className="text-xs text-muted-foreground mt-1">Ако е изключено, грамажът НЕ се показва на клиентите (полезно за неточни стойности).</p>
                                </div>
                                
                                {/* Prepayment Rule Section */}
                                <div className="col-span-2 p-3 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20">
                                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="prepayment-required-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.requires_prepayment_outside_sofia || false}
                                            onChange={(e) => setFormData({ ...formData, requires_prepayment_outside_sofia: e.target.checked })}
                                            className="h-4 w-4 accent-sky-600"
                                            data-testid="prepayment-required-checkbox"
                                        />
                                        <span className="text-sm font-medium">🧊 Изисква предварително плащане при доставка извън София</span>
                                    </label>
                                    <p className="text-xs text-muted-foreground mt-1">За хладилни (месни/млечни) продукти — наложен платеж няма да е достъпен при доставка извън София. Активира се автоматично при избор на подкатегория „Месни продукти" или „Млечни продукти".</p>
                                </div>
                                
                                {/* Image Upload Section */}
                                <div className="col-span-2">
                                    <Label>Снимка *</Label>
                                    <div className="mt-2 space-y-3">
                                        {/* Image Preview (falls back to current product image when editing) */}
                                        {(formData.image_url || editingProduct?.thumbnail_url) && (
                                            <div className="relative w-32 h-32 rounded-lg overflow-hidden border dark:border-slate-600">
                                                <img src={formData.image_url || editingProduct?.thumbnail_url} alt="Preview" className="w-full h-full object-cover" />
                                                {formData.image_url && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, image_url: '' })}
                                                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Upload Button */}
                                        <div>
                                            <input 
                                                type="file" 
                                                ref={fileInputRef}
                                                onChange={handleImageUpload}
                                                accept="image/*"
                                                className="hidden"
                                            />
                                            <Button 
                                                type="button"
                                                variant="outline"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploading}
                                                className="dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
                                                data-testid="upload-image-btn"
                                            >
                                                {uploading ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                                                        Качване...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {formData.image_url ? 'Смени снимката' : 'Качи снимка от компютъра'}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                        <p className="text-xs text-muted-foreground dark:text-slate-500">
                                            Поддържани формати: JPG, PNG, WebP (макс. 5MB)
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="col-span-2">
                                    <Label>Описание *</Label>
                                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} />
                                </div>

                                {/* AI actions for this product */}
                                {editingProduct && (
                                    <div className="col-span-2 p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10">
                                        <Label className="text-xs text-purple-600 dark:text-purple-400 mb-2 block flex items-center gap-1">
                                            <Sparkles className="h-3 w-3" /> AI действия (изискват запазена снимка)
                                        </Label>
                                        <div className="flex flex-wrap gap-2">
                                            {Object.entries(AI_MODE_LABELS).map(([mode, label]) => (
                                                <Button
                                                    key={mode}
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={aiLoadingId === editingProduct.product_id}
                                                    onClick={() => aiSuggestForForm(mode)}
                                                    className="text-xs border-purple-300 text-purple-700 dark:text-purple-300 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/30"
                                                    data-testid={`form-ai-${mode}-btn`}
                                                >
                                                    {aiLoadingId === editingProduct.product_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                                                    {label}
                                                </Button>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                                            „Попълни само празните полета“ никога не презаписва ръчно въведени данни. Режимите „Пренапиши…“ искат потвърждение.
                                        </p>
                                    </div>
                                )}

                                {/* Extra product info (collapsible) */}
                                <div className="col-span-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowExtraFields(!showExtraFields)}
                                        className="w-full flex items-center justify-between p-2 rounded-lg border dark:border-slate-600 text-sm font-medium dark:text-white hover:bg-slate-50 dark:hover:bg-slate-700/50"
                                        data-testid="toggle-extra-fields-btn"
                                    >
                                        <span>📋 Допълнителна информация (състав, SEO, баркод…)</span>
                                        <span>{showExtraFields ? '▲' : '▼'}</span>
                                    </button>
                                    {showExtraFields && (
                                        <div className="grid grid-cols-2 gap-3 mt-3 p-3 border rounded-lg dark:border-slate-700" data-testid="extra-fields-section">
                                            <div>
                                                <Label className="text-xs">Оригинално име (от опаковката)</Label>
                                                <Input value={formData.original_name} onChange={(e) => setFormData({ ...formData, original_name: e.target.value })} data-testid="product-original-name-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Държава на произход</Label>
                                                <Input value={formData.origin_country} onChange={(e) => setFormData({ ...formData, origin_country: e.target.value })} placeholder="напр. Турция" data-testid="product-origin-input" />
                                            </div>
                                            <div className="col-span-2">
                                                <Label className="text-xs">Кратко описание</Label>
                                                <Textarea value={formData.short_description} onChange={(e) => setFormData({ ...formData, short_description: e.target.value })} rows={2} data-testid="product-short-desc-input" />
                                            </div>
                                            <div className="col-span-2">
                                                <Label className="text-xs">Състав</Label>
                                                <Textarea value={formData.composition} onChange={(e) => setFormData({ ...formData, composition: e.target.value })} rows={2} data-testid="product-composition-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Алергени</Label>
                                                <Input value={formData.allergens} onChange={(e) => setFormData({ ...formData, allergens: e.target.value })} data-testid="product-allergens-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Хранителни стойности</Label>
                                                <Input value={formData.nutrition} onChange={(e) => setFormData({ ...formData, nutrition: e.target.value })} data-testid="product-nutrition-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Начин на употреба</Label>
                                                <Input value={formData.usage_instructions} onChange={(e) => setFormData({ ...formData, usage_instructions: e.target.value })} data-testid="product-usage-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Предупреждения</Label>
                                                <Input value={formData.warnings} onChange={(e) => setFormData({ ...formData, warnings: e.target.value })} data-testid="product-warnings-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Баркод</Label>
                                                <Input value={formData.barcode} onChange={(e) => setFormData({ ...formData, barcode: e.target.value })} data-testid="product-barcode-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">SKU</Label>
                                                <Input value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} data-testid="product-sku-input" />
                                            </div>
                                            <div className="col-span-2">
                                                <Label className="text-xs">Тагове (разделени със запетая)</Label>
                                                <Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} placeholder="омекотител, пране, аромат" data-testid="product-tags-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">SEO заглавие</Label>
                                                <Input value={formData.seo_title} onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })} data-testid="product-seo-title-input" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Slug (URL)</Label>
                                                <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} data-testid="product-slug-input" />
                                            </div>
                                            <div className="col-span-2">
                                                <Label className="text-xs">SEO описание</Label>
                                                <Textarea value={formData.seo_description} onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })} rows={2} data-testid="product-seo-desc-input" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-6">
                                <label className="flex items-center gap-2">
                                    <Switch checked={formData.is_visible} onCheckedChange={(c) => setFormData({ ...formData, is_visible: c })} />
                                    <span>Видим</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <Switch checked={formData.is_featured} onCheckedChange={(c) => setFormData({ ...formData, is_featured: c })} />
                                    <span>Топ продукт</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <Switch checked={formData.is_new} onCheckedChange={(c) => setFormData({ ...formData, is_new: c })} />
                                    <span>Нов</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <Switch checked={formData.is_bestseller} onCheckedChange={(c) => setFormData({ ...formData, is_bestseller: c })} />
                                    <span>Бестселър</span>
                                </label>
                            </div>
                            <div className="flex gap-3">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    className="flex-1 dark:border-slate-600 dark:text-slate-300"
                                    onClick={(e) => handleSubmit(e, true)}
                                >
                                    💾 Запази като чернова
                                </Button>
                                <Button type="submit" className="flex-1 btn-primary" data-testid="save-product-btn">
                                    {editingProduct ? 'Запази промените' : 'Създай продукт'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

                        {/* Bulk Image Import → Draft Products Dialog (new component, up to 100 images) */}
                        <BulkImportDialog
                            open={showBulkImportDialog}
                            onClose={() => setShowBulkImportDialog(false)}
                            token={token || localStorage.getItem('token')}
                            onDone={() => { fetchData(); fetchDuplicatesCount(); }}
                            onShowDuplicates={() => setShowDuplicates(true)}
                        />

                        {/* Duplicates Manager */}
                        <DuplicatesManager
                            open={showDuplicates}
                            onClose={() => setShowDuplicates(false)}
                            token={token || localStorage.getItem('token')}
                            onChanged={() => { fetchData(); fetchDuplicatesCount(); }}
                        />

            {/* ===== AI Fill Selected Confirmation Dialog ===== */}
            <Dialog open={showAiSelectedDialog} onOpenChange={setShowAiSelectedDialog}>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg" data-testid="ai-fill-selected-dialog">
                    <DialogHeader>
                        <DialogTitle className="dark:text-white flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-purple-500" />
                            AI попълни избраните чернови
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-sm dark:text-slate-300">
                            Ще стартираме AI попълване за <strong>{selectedProducts.length}</strong> избрани продукта.
                        </p>
                        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-200">
                            ⚠️ Обработката може да промени данните на продуктите. AI ще анализира снимката на всяка чернова и ще попълни: име, описание, марка, категория, тегло.
                            <br/><br/>
                            По подразбиране <strong>не се презаписват</strong> ръчно попълнени полета.
                        </div>
                        <label className="flex items-center gap-2 p-3 rounded border dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/40">
                            <input
                                type="checkbox"
                                checked={aiSelectedOverwrite}
                                onChange={(e) => setAiSelectedOverwrite(e.target.checked)}
                                className="h-4 w-4 accent-red-500"
                                data-testid="ai-fill-overwrite-checkbox"
                            />
                            <span className="text-sm dark:text-white">
                                Презапиши съществуващите данни (използвайте с внимание!)
                            </span>
                        </label>
                        <div className="flex gap-2 justify-end pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowAiSelectedDialog(false)}
                                disabled={aiSelectedStarting}
                                className="dark:border-slate-600"
                                data-testid="ai-fill-selected-cancel-btn"
                            >
                                Отказ
                            </Button>
                            <Button
                                onClick={startAiFillSelected}
                                disabled={aiSelectedStarting || selectedProducts.length === 0}
                                className="btn-primary bg-purple-600 hover:bg-purple-700 text-white"
                                data-testid="ai-fill-selected-start-btn"
                            >
                                {aiSelectedStarting ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Стартиране...</>
                                ) : (
                                    <><Sparkles className="h-4 w-4 mr-2" />Стартирай</>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== AI Fill Progress Panel ===== */}
            <Dialog open={showAiProgressPanel} onOpenChange={setShowAiProgressPanel}>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ai-fill-progress-panel">
                    <DialogHeader>
                        <DialogTitle className="dark:text-white flex items-center gap-2">
                            <Sparkles className={`h-5 w-5 text-purple-500 ${aiFill?.running ? 'animate-pulse' : ''}`} />
                            AI попълване — прогрес
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
                        {/* Summary */}
                        <div className="flex items-center justify-between text-sm dark:text-white">
                            <span data-testid="ai-progress-summary">
                                Обработени <strong>{aiFill?.done || 0}</strong> от <strong>{aiFill?.total || 0}</strong>
                                {!!aiFill?.errors && <span className="text-red-500 ml-2">({aiFill.errors} грешки/проверки)</span>}
                                {aiFill?.stop_requested && <span className="text-amber-500 ml-2">⏹️ Спрян</span>}
                            </span>
                            {aiFill?.running ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={stopAiFill}
                                    disabled={aiStopping || aiFill?.stop_requested}
                                    className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:text-amber-400"
                                    data-testid="ai-fill-stop-btn"
                                >
                                    {aiStopping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
                                    Спри безопасно
                                </Button>
                            ) : (aiFill?.total > 0) && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setAiProgressErrorsOnly(!aiProgressErrorsOnly)}
                                        className="dark:border-slate-600 dark:text-white"
                                        data-testid="ai-fill-toggle-errors-btn"
                                    >
                                        {aiProgressErrorsOnly ? 'Покажи всички' : 'Виж само с грешки'}
                                    </Button>
                                </div>
                            )}
                        </div>
                        {/* Progress bar */}
                        <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                            <div
                                className="h-full bg-purple-500 transition-all"
                                style={{ width: `${aiFill?.total ? ((aiFill.done / aiFill.total) * 100) : 0}%` }}
                            />
                        </div>
                        {/* Per-product list */}
                        <div className="flex-1 overflow-y-auto border rounded dark:border-slate-700 divide-y dark:divide-slate-700" data-testid="ai-progress-list">
                            {(() => {
                                const entries = Object.entries(aiFill?.products || {});
                                const filtered = aiProgressErrorsOnly
                                    ? entries.filter(([, v]) => ['error', 'needs_review', 'partial', 'possible_duplicate'].includes(v.status))
                                    : entries;
                                if (filtered.length === 0) {
                                    return (
                                        <div className="p-4 text-sm text-center text-slate-500 dark:text-slate-400">
                                            {aiProgressErrorsOnly ? 'Няма продукти с грешки / нужна проверка.' : 'Няма данни за показване.'}
                                        </div>
                                    );
                                }
                                const statusMeta = {
                                    pending:      { emoji: '⏳', color: 'text-slate-500', label: 'Чака' },
                                    processing:   { emoji: '⚙️', color: 'text-blue-500 animate-pulse', label: 'Обработва се' },
                                    success:      { emoji: '✅', color: 'text-green-600', label: 'Успех' },
                                    partial:      { emoji: '⚠️', color: 'text-amber-600', label: 'Частично' },
                                    skipped:      { emoji: '⏭️', color: 'text-slate-400', label: 'Пропуснат' },
                                    error:        { emoji: '❌', color: 'text-red-600', label: 'Грешка' },
                                    needs_review: { emoji: '🔍', color: 'text-fuchsia-600', label: 'Ръчна проверка' },
                                    possible_duplicate: { emoji: '👯', color: 'text-orange-600', label: 'Възможен дубликат' },
                                };
                                return filtered.map(([pid, v]) => {
                                    const meta = statusMeta[v.status] || statusMeta.pending;
                                    const prod = products.find(p => p.product_id === pid);
                                    const label = prod?.name || pid;
                                    return (
                                        <div key={pid} className="flex items-start gap-2 p-2 text-sm" data-testid={`ai-progress-item-${pid}`}>
                                            <span className={`${meta.color} shrink-0`}>{meta.emoji}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium dark:text-white truncate">{label}</div>
                                                <div className={`text-xs ${meta.color}`}>{meta.label}{v.message ? `: ${v.message}` : ''}</div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        {/* Summary footer when done */}
                        {aiFill && !aiFill.running && aiFill.total > 0 && (() => {
                            const entries = Object.entries(aiFill.products || {});
                            const counts = { success: 0, partial: 0, skipped: 0, error: 0, needs_review: 0, possible_duplicate: 0 };
                            entries.forEach(([, v]) => { if (counts[v.status] !== undefined) counts[v.status] += 1; });
                            return (
                                <div className="text-xs grid grid-cols-2 md:grid-cols-6 gap-2 pt-2 border-t dark:border-slate-700" data-testid="ai-fill-summary">
                                    <span className="text-green-600">✅ Успех: {counts.success}</span>
                                    <span className="text-amber-600">⚠️ Частично: {counts.partial}</span>
                                    <span className="text-slate-500">⏭️ Пропуснати: {counts.skipped}</span>
                                    <span className="text-red-600">❌ Грешки: {counts.error}</span>
                                    <span className="text-fuchsia-600">🔍 Ръчна проверка: {counts.needs_review}</span>
                                    <span className="text-orange-600">👯 Дубликати: {counts.possible_duplicate}</span>
                                </div>
                            );
                        })()}
                        <div className="flex justify-end pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowAiProgressPanel(false)}
                                className="dark:border-slate-600"
                                data-testid="ai-fill-progress-close-btn"
                            >
                                Затвори
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== Bulk Delete Confirmation Dialog ===== */}
            <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-md" data-testid="bulk-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="dark:text-white flex items-center gap-2 text-red-600">
                            <Trash2 className="h-5 w-5" />
                            Изтриване на продукти
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <p className="text-sm dark:text-slate-300">
                            Наистина ли искате да изтриете <strong>{selectedProducts.length}</strong> продукта?
                        </p>
                        <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 p-3 text-sm text-red-800 dark:text-red-200">
                            ⚠️ Това действие е <strong>необратимо</strong>. Ще бъдат изтрити и всички свързани снимки.
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => setShowBulkDeleteDialog(false)}
                                disabled={bulkDeleting}
                                className="dark:border-slate-600"
                                data-testid="bulk-delete-cancel-btn"
                            >
                                Отказ
                            </Button>
                            <Button
                                onClick={bulkDeleteSelected}
                                disabled={bulkDeleting}
                                className="bg-red-600 hover:bg-red-700 text-white"
                                data-testid="bulk-delete-confirm-btn"
                            >
                                {bulkDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Изтриване...</> : <><Trash2 className="h-4 w-4 mr-2" />Изтрий</>}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== Bulk Stock Dialog ===== */}
            <Dialog open={showBulkStockDialog} onOpenChange={setShowBulkStockDialog}>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-md" data-testid="bulk-stock-dialog">
                    <DialogHeader>
                        <DialogTitle className="dark:text-white flex items-center gap-2">
                            <Package className="h-5 w-5 text-blue-500" />
                            Промяна на наличност
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <p className="text-sm dark:text-slate-300">
                            Ще зададете наличност за <strong>{selectedProducts.length}</strong> продукта.
                        </p>
                        <div>
                            <Label className="dark:text-slate-300">Наличност (брой)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={bulkStockValue}
                                onChange={(e) => setBulkStockValue(e.target.value)}
                                placeholder="напр. 10"
                                className="mt-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                data-testid="bulk-stock-input"
                            />
                        </div>
                        <div className="flex gap-2 justify-end pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowBulkStockDialog(false)}
                                disabled={bulkStockSaving}
                                className="dark:border-slate-600"
                                data-testid="bulk-stock-cancel-btn"
                            >
                                Отказ
                            </Button>
                            <Button
                                onClick={bulkSetStock}
                                disabled={bulkStockSaving || bulkStockValue === ''}
                                className="btn-primary bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="bulk-stock-save-btn"
                            >
                                {bulkStockSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Запазване...</> : <><Check className="h-4 w-4 mr-2" />Приложи</>}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bulk Brand Dialog */}
            <Dialog open={showBulkBrandDialog} onOpenChange={setShowBulkBrandDialog}>
                <DialogContent onCloseAutoFocus={(e) => e.preventDefault()} className="dark:bg-slate-800 dark:border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="dark:text-white">Задай марка на избраните продукти</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-sm text-muted-foreground dark:text-slate-400">
                            Ще промените марката на <strong>{selectedProducts.length}</strong> избрани продукта. Марката автоматично ще се свърже с категориите на продуктите.
                        </p>
                        <Select value={bulkBrandValue} onValueChange={setBulkBrandValue}>
                            <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="bulk-brand-select">
                                <SelectValue placeholder="Изберете марка..." />
                            </SelectTrigger>
                            <SelectContent className="dark:bg-slate-800">
                                <SelectItem value="__remove__" className="text-red-500">Премахни марката</SelectItem>
                                {brands.map(b => (
                                    <SelectItem key={b.brand_id} value={b.brand_id} className="dark:text-white">{b.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="flex gap-2 justify-end pt-2">
                            <Button variant="outline" onClick={() => setShowBulkBrandDialog(false)} disabled={bulkBrandSaving} className="dark:border-slate-600" data-testid="bulk-brand-cancel-btn">
                                Отказ
                            </Button>
                            <Button onClick={bulkSetBrand} disabled={bulkBrandSaving || !bulkBrandValue} className="btn-primary" data-testid="bulk-brand-save-btn">
                                {bulkBrandSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Запазване...</> : <><Check className="h-4 w-4 mr-2" />Приложи</>}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Auto-assign brands report dialog */}
            <Dialog open={!!autoAssignReport} onOpenChange={(open) => !open && setAutoAssignReport(null)}>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="dark:text-white">Отчет: Автоматично разпределяне по марки</DialogTitle>
                    </DialogHeader>
                    {autoAssignReport && (
                        <div className="space-y-3 text-sm" data-testid="auto-assign-report">
                            <p className="dark:text-slate-300">
                                Разпределени продукти: <strong className="text-emerald-600">{autoAssignReport.products_assigned}</strong><br />
                                Нови марки: <strong>{autoAssignReport.created_brands.length}</strong> · Съществуващи: <strong>{autoAssignReport.linked_existing_brands.length}</strong>
                            </p>
                            {autoAssignReport.created_brands.length > 0 && (
                                <div>
                                    <p className="font-medium dark:text-white mb-1">Създадени марки:</p>
                                    <div className="max-h-48 overflow-y-auto space-y-1 bg-slate-50 dark:bg-slate-700 rounded p-2">
                                        {autoAssignReport.created_brands.map((b, i) => (
                                            <p key={i} className="text-xs dark:text-slate-300">
                                                • <strong>{b.name}</strong> — {b.products} продукта
                                                {b.variants.length > 1 && <span className="text-muted-foreground"> (обединени: {b.variants.join(', ')})</span>}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {autoAssignReport.skipped_texts?.length > 0 && (
                                <p className="text-xs text-amber-600">Пропуснати (несигурни): {autoAssignReport.skipped_texts.join(', ')}</p>
                            )}
                            <Button onClick={() => setAutoAssignReport(null)} className="btn-primary w-full" data-testid="auto-assign-report-close-btn">Готово</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Filter and Bulk Actions Bar */}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700">
                <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium dark:text-slate-300">Филтър по категория:</Label>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600">
                            <SelectValue placeholder="Всички категории" />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800">
                            <SelectItem value="all" className="dark:text-white">Всички категории</SelectItem>
                            {categories.filter(c => c.category_id).map(c => (
                                <SelectItem key={c.category_id} value={c.category_id} className="dark:text-white">
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={filterNoBrand} onCheckedChange={(v) => setFilterNoBrand(!!v)} data-testid="filter-no-brand-checkbox" />
                    <span className="text-sm font-medium dark:text-slate-300">Продукти без марка</span>
                </label>

                <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium dark:text-slate-300">Марка:</Label>
                    <Select value={filterBrand} onValueChange={setFilterBrand}>
                        <SelectTrigger className="w-44 dark:bg-slate-700 dark:border-slate-600" data-testid="filter-brand-select">
                            <SelectValue placeholder="Всички марки" />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800">
                            <SelectItem value="all" className="dark:text-white">Всички марки</SelectItem>
                            <SelectItem value="none" className="dark:text-white">Без марка</SelectItem>
                            {brands.map(b => (
                                <SelectItem key={b.brand_id} value={b.brand_id} className="dark:text-white">{b.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={runAutoAssignBrands}
                    disabled={autoAssignRunning}
                    className="dark:border-slate-600 border-purple-500 text-purple-600 hover:bg-purple-50 dark:text-purple-400"
                    data-testid="auto-assign-brands-btn"
                    title="Създава марки от текстовите марки на продуктите и ги разпределя автоматично"
                >
                    {autoAssignRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Tags className="h-4 w-4 mr-1" />}
                    Разпредели по марки
                </Button>

                {/* Bulk selection helper buttons — always visible */}
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={selectUpTo100Filtered}
                        disabled={filteredProducts.length === 0}
                        className="dark:border-slate-600 dark:text-white"
                        data-testid="select-up-to-100-btn"
                        title={`Избери до 100 от ${filteredProducts.length} филтрирани`}
                    >
                        <Check className="h-4 w-4 mr-1" />
                        Избери до 100 от филтрирани
                    </Button>
                    {selectedProducts.length > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedProducts([])}
                            className="text-red-500 hover:text-red-600"
                            data-testid="clear-selection-btn"
                        >
                            <X className="h-4 w-4 mr-1" />
                            Изчисти
                        </Button>
                    )}
                </div>

                {selectedProducts.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap ml-auto">
                        <Badge
                            variant="secondary"
                            className={`${selectedProducts.length >= MAX_BULK_SELECT ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200' : 'dark:bg-slate-700'}`}
                            data-testid="selection-counter"
                        >
                            Избрани: {selectedProducts.length} от {MAX_BULK_SELECT}
                        </Badge>

                        {/* 🎯 AI Fill Selected — most important */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setAiSelectedOverwrite(false); setShowAiSelectedDialog(true); }}
                            disabled={aiFill?.running}
                            className="dark:border-slate-600 border-purple-500 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20"
                            data-testid="ai-fill-selected-btn"
                            title="AI попълни само избраните чернови"
                        >
                            <Sparkles className="h-4 w-4 mr-1" />
                            AI попълни избраните
                        </Button>

                        {/* Publish selected */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkPublishSelected}
                            className="dark:border-slate-600 border-green-500 text-green-600 hover:bg-green-50 dark:text-green-400"
                            data-testid="bulk-publish-selected-btn"
                        >
                            <Check className="h-4 w-4 mr-1" />
                            Публикувай
                        </Button>

                        {/* Move to Draft */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkDraftSelected}
                            className="dark:border-slate-600 dark:text-white"
                            data-testid="bulk-draft-selected-btn"
                            title="Премести в статус Чернова"
                        >
                            <FileText className="h-4 w-4 mr-1" />
                            В чернова
                        </Button>

                        {/* Show/Hide */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkShowSelected}
                            className="dark:border-slate-600 dark:text-white"
                            data-testid="bulk-show-selected-btn"
                        >
                            <Eye className="h-4 w-4 mr-1" />
                            Покажи
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkHideSelected}
                            className="dark:border-slate-600 dark:text-white"
                            data-testid="bulk-hide-selected-btn"
                        >
                            <EyeOff className="h-4 w-4 mr-1" />
                            Скрий
                        </Button>

                        {/* Stock */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setBulkStockValue(''); setShowBulkStockDialog(true); }}
                            className="dark:border-slate-600 dark:text-white"
                            data-testid="bulk-stock-selected-btn"
                        >
                            <Package className="h-4 w-4 mr-1" />
                            Наличност
                        </Button>

                        {/* Brand */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setBulkBrandValue(''); setShowBulkBrandDialog(true); }}
                            className="dark:border-slate-600 dark:text-white"
                            data-testid="bulk-brand-selected-btn"
                        >
                            <Tags className="h-4 w-4 mr-1" />
                            Марка
                        </Button>

                        {/* Delete */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowBulkDeleteDialog(true)}
                            className="dark:border-slate-600 border-red-500 text-red-600 hover:bg-red-50 dark:text-red-400"
                            data-testid="bulk-delete-selected-btn"
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Изтрий
                        </Button>
                    </div>
                )}

                {selectedProducts.length > 0 && (
                    <div className="flex items-center gap-3 ml-auto w-full mt-2 flex-wrap">
                        {/* Bulk Duplicate Button */}
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={bulkDuplicateProducts}
                            className="dark:border-slate-600 dark:text-white"
                        >
                            <Copy className="h-4 w-4 mr-2" />
                            Дублирай
                        </Button>
                        
                        {/* Bulk Price Change Dialog */}
                        <Dialog open={showBulkPriceDialog} onOpenChange={setShowBulkPriceDialog}>
                            <DialogTrigger asChild>
                                <Button type="button" variant="outline" size="sm" className="dark:border-slate-600 dark:text-white">
                                    <Percent className="h-4 w-4 mr-2" />
                                    Промени цените
                                </Button>
                            </DialogTrigger>
                            <DialogContent onCloseAutoFocus={(e) => e.preventDefault()} className="dark:bg-slate-800 dark:border-slate-700">
                                <DialogHeader>
                                    <DialogTitle className="dark:text-white">Масова промяна на цени</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                    <p className="text-sm text-muted-foreground dark:text-slate-400">
                                        Ще промените цените на <strong>{selectedProducts.length}</strong> продукта
                                    </p>
                                    <div className="flex gap-2">
                                        <Button 
                                            type="button"
                                            variant={bulkPriceAction === 'percent' ? 'default' : 'outline'}
                                            onClick={() => setBulkPriceAction('percent')}
                                            className="flex-1"
                                        >
                                            Процент %
                                        </Button>
                                        <Button 
                                            type="button"
                                            variant={bulkPriceAction === 'fixed' ? 'default' : 'outline'}
                                            onClick={() => setBulkPriceAction('fixed')}
                                            className="flex-1"
                                        >
                                            Фиксирана сума
                                        </Button>
                                    </div>
                                    <div>
                                        <Label className="dark:text-slate-300">
                                            {bulkPriceAction === 'percent' ? 'Процент (+ увеличение, - намаление)' : 'Сума в EUR (+ или -)'}
                                        </Label>
                                        <Input 
                                            type="number" 
                                            step="0.01"
                                            value={bulkPriceValue}
                                            onChange={(e) => setBulkPriceValue(e.target.value)}
                                            placeholder={bulkPriceAction === 'percent' ? 'напр. 10 за +10%' : 'напр. 5 за +5€'}
                                            className="mt-2 dark:bg-slate-700 dark:border-slate-600"
                                        />
                                    </div>
                                    <div className="flex gap-3 pt-4">
                                        <Button onClick={applyBulkPriceChange} className="flex-1 btn-primary">
                                            <Check className="h-4 w-4 mr-2" />
                                            Приложи
                                        </Button>
                                        <Button variant="outline" onClick={() => setShowBulkPriceDialog(false)} className="dark:border-slate-600">
                                            Отказ
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
                            <DialogTrigger asChild>
                                <Button type="button" variant="outline" size="sm" className="dark:border-slate-600 dark:text-white">
                                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                                    Премести в категория
                                </Button>
                            </DialogTrigger>
                            <DialogContent onCloseAutoFocus={(e) => e.preventDefault()} className="dark:bg-slate-800 dark:border-slate-700">
                                <DialogHeader>
                                    <DialogTitle className="dark:text-white">Премести {selectedProducts.length} продукта</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                    <div>
                                        <Label className="dark:text-slate-300">Нова категория</Label>
                                        <Select value={targetCategory} onValueChange={(v) => { setTargetCategory(v); setTargetSubcategory(''); }}>
                                            <SelectTrigger className="mt-2 dark:bg-slate-700 dark:border-slate-600">
                                                <SelectValue placeholder="Избери категория" />
                                            </SelectTrigger>
                                            <SelectContent className="dark:bg-slate-800">
                                                {categories.filter(c => c.category_id).map(c => (
                                                    <SelectItem key={c.category_id} value={c.category_id} className="dark:text-white">
                                                        {c.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {targetCategoryData?.subcategories?.length > 0 && (
                                        <div>
                                            <Label className="dark:text-slate-300">Подкатегория (по избор)</Label>
                                            <Select value={targetSubcategory || '__none__'} onValueChange={(v) => setTargetSubcategory(v === '__none__' ? '' : v)}>
                                                <SelectTrigger className="mt-2 dark:bg-slate-700 dark:border-slate-600">
                                                    <SelectValue placeholder="Избери подкатегория" />
                                                </SelectTrigger>
                                                <SelectContent className="dark:bg-slate-800">
                                                    <SelectItem value="__none__" className="dark:text-white">Без подкатегория</SelectItem>
                                                    {targetCategoryData.subcategories.filter(s => s && s.trim()).map(s => (
                                                        <SelectItem key={s} value={s} className="dark:text-white">{s}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <div className="flex gap-3 pt-4">
                                        <Button type="button" onClick={bulkMoveProducts} className="flex-1 btn-primary">
                                            <Check className="h-4 w-4 mr-2" />
                                            Премести
                                        </Button>
                                        <Button type="button" variant="outline" onClick={() => setShowMoveDialog(false)} className="dark:border-slate-600">
                                            <X className="h-4 w-4 mr-2" />
                                            Отказ
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setSelectedProducts([])}
                            className="dark:text-slate-400"
                        >
                            Изчисти
                        </Button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="animate-pulse space-y-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-20 bg-muted dark:bg-slate-700 rounded" />)}
                </div>
            ) : productsViewMode === 'folders' ? (
                /* FOLDER VIEW */
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 overflow-hidden">
                    {/* Expand/Collapse All */}
                    <div className="flex justify-end gap-2 p-3 bg-muted/50 dark:bg-slate-700/50 border-b dark:border-slate-700">
                        <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs">
                            <ChevronDown className="h-3 w-3 mr-1" />
                            Разгъни всички
                        </Button>
                        <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs">
                            <ChevronRight className="h-3 w-3 mr-1" />
                            Свий всички
                        </Button>
                    </div>
                    
                    <div className="divide-y divide-border dark:divide-slate-700">
                        {Object.entries(groupedProducts).map(([catId, group]) => {
                            const isExpanded = expandedCategories[catId];
                            const productCount = group.uncategorized.length + 
                                Object.values(group.subcategories).reduce((sum, arr) => sum + arr.length, 0);
                            
                            return (
                                <div key={catId} className="bg-white dark:bg-slate-800">
                                    {/* Category Header */}
                                    <button
                                        onClick={() => toggleCategory(catId)}
                                        className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 dark:hover:bg-slate-700/50 transition-colors text-left"
                                    >
                                        {isExpanded ? (
                                            <FolderOpen className="h-5 w-5 text-amber-500" />
                                        ) : (
                                            <Folder className="h-5 w-5 text-amber-500" />
                                        )}
                                        <span className="font-semibold dark:text-white">{group.category.name}</span>
                                        <Badge variant="secondary" className="ml-auto">{productCount} продукта</Badge>
                                        {isExpanded ? (
                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        )}
                                    </button>
                                    
                                    {/* Category Content */}
                                    {isExpanded && (
                                        <div className="pl-6 pb-2">
                                            {/* Subcategories */}
                                            {Object.entries(group.subcategories).map(([subcat, subProducts]) => {
                                                const subKey = `${catId}-${subcat}`;
                                                const isSubExpanded = expandedSubcategories[subKey];
                                                
                                                return (
                                                    <div key={subcat} className="border-l-2 border-amber-200 dark:border-amber-700 ml-2">
                                                        <button
                                                            onClick={() => toggleSubcategory(catId, subcat)}
                                                            className="w-full flex items-center gap-2 p-2 pl-4 hover:bg-muted/30 dark:hover:bg-slate-700/30 text-left"
                                                        >
                                                            {isSubExpanded ? (
                                                                <FolderOpen className="h-4 w-4 text-amber-400" />
                                                            ) : (
                                                                <Folder className="h-4 w-4 text-amber-400" />
                                                            )}
                                                            <span className="text-sm font-medium dark:text-slate-200">{subcat}</span>
                                                            <Badge variant="outline" className="ml-auto text-xs">{subProducts.length}</Badge>
                                                            {isSubExpanded ? (
                                                                <ChevronDown className="h-3 w-3 text-muted-foreground" />
                                                            ) : (
                                                                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                                            )}
                                                        </button>
                                                        
                                                        {isSubExpanded && (
                                                            <div className="ml-4 pl-4 py-1 space-y-1">
                                                                {renderBrandGrouped(subProducts, subKey, (product) => (
                                                                    <div 
                                                                        key={product.product_id}
                                                                        className={`flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 dark:hover:bg-slate-700/50 ${product.is_visible === false ? 'opacity-50' : ''} ${product.is_draft ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                                                                    >
                                                                        <Checkbox 
                                                                            checked={selectedProducts.includes(product.product_id)}
                                                                            onCheckedChange={() => toggleProductSelection(product.product_id)}
                                                                            className="dark:border-slate-500"
                                                                        />
                                                                        {product.thumbnail_url ? (
                                                                            <img src={product.thumbnail_url} alt="" className="w-10 h-10 object-cover rounded" />
                                                                        ) : (
                                                                            <div className="w-10 h-10 bg-muted dark:bg-slate-600 rounded flex items-center justify-center">
                                                                                <Package className="h-5 w-5 text-muted-foreground" />
                                                                            </div>
                                                                        )}
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium dark:text-white truncate">{product.name}</p>
                                                                            <p className="text-xs text-muted-foreground">{product.price?.toFixed(2)} €</p>
                                                                        </div>
                                                                        <div className="flex gap-1">
                                                                            <Button variant="ghost" size="sm" onClick={() => duplicateProduct(product)} title="Дублирай">
                                                                                <Copy className="h-3 w-3 text-blue-500" />
                                                                            </Button>
                                                                            <Button variant="ghost" size="sm" onClick={() => openEdit(product)} title="Редактирай" data-testid={`edit-product-${product.product_id}`}>
                                                                                <Edit2 className="h-3 w-3" />
                                                                            </Button>
                                                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(product.product_id)} title="Изтрий">
                                                                                <Trash2 className="h-3 w-3 text-red-500" />
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            
                                            {/* Uncategorized products in this category */}
                                            {group.uncategorized.length > 0 && (
                                                <div className="ml-2 pl-4 py-1 space-y-1 border-l-2 border-slate-200 dark:border-slate-600">
                                                    <p className="text-xs text-muted-foreground pl-2 py-1">Без подкатегория</p>
                                                    {renderBrandGrouped(group.uncategorized, `${catId}::nosub`, (product) => {
                                                        const issues = getProductIssues(product);
                                                        const hasIssues = issues.length > 0;
                                                        return (
                                                        <div 
                                                            key={product.product_id}
                                                            className={`flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 dark:hover:bg-slate-700/50 ${product.is_visible === false ? 'opacity-50' : ''} ${hasIssues || product.is_draft ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                                                        >
                                                            <Checkbox 
                                                                checked={selectedProducts.includes(product.product_id)}
                                                                onCheckedChange={() => toggleProductSelection(product.product_id)}
                                                                className="dark:border-slate-500"
                                                            />
                                                            {product.thumbnail_url || product.image_url || product.base64_image ? (
                                                                <img src={product.thumbnail_url || product.image_url || product.base64_image} alt="" className="w-10 h-10 object-cover rounded" />
                                                            ) : (
                                                                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded flex items-center justify-center border border-dashed border-red-300">
                                                                    <Package className="h-5 w-5 text-red-400" />
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium dark:text-white truncate">
                                                                    {product.name || <span className="text-red-500 italic">⚠️ Без име</span>}
                                                                </p>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <p className="text-xs text-muted-foreground">
                                                                        {product.price ? `${product.price?.toFixed(2)} €` : <span className="text-red-500">⚠️ Без цена</span>}
                                                                    </p>
                                                                    {hasIssues && issues.map((issue, i) => (
                                                                        <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
                                                                            ⚠️ {issue}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <Button variant="ghost" size="sm" onClick={() => duplicateProduct(product)} title="Дублирай">
                                                                    <Copy className="h-3 w-3 text-blue-500" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => openEdit(product)} title="Редактирай" data-testid={`edit-product-${product.product_id}`}>
                                                                    <Edit2 className="h-3 w-3" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(product.product_id)} title="Изтрий">
                                                                    <Trash2 className="h-3 w-3 text-red-500" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )})}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                /* LIST VIEW - Original Table */
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-muted dark:bg-slate-700">
                            <tr>
                                <th className="w-12 p-4">
                                    <Checkbox 
                                        checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                                        onCheckedChange={selectAllProducts}
                                        className="dark:border-slate-500"
                                    />
                                </th>
                                <th className="text-left p-4 dark:text-slate-200">Продукт</th>
                                <th className="text-left p-4 dark:text-slate-200">Категория</th>
                                <th className="text-left p-4 dark:text-slate-200">Цена</th>
                                <th className="text-left p-4 dark:text-slate-200">Флагове</th>
                                <th className="text-left p-4 dark:text-slate-200">Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedProducts.map((product) => {
                                const productCategory = categories.find(c => c.category_id === product.category_id);
                                const issues = getProductIssues(product);
                                const hasIssues = issues.length > 0;
                                const isDraft = product.is_draft;
                                return (
                                <tr key={product.product_id} className={`border-t border-border dark:border-slate-700 ${product.is_visible === false ? 'opacity-50' : ''} ${hasIssues || isDraft ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                                    <td className="p-4">
                                        <Checkbox 
                                            checked={selectedProducts.includes(product.product_id)}
                                            onCheckedChange={() => toggleProductSelection(product.product_id)}
                                            className="dark:border-slate-500"
                                        />
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-start gap-4">
                                            {product.thumbnail_url || product.image_url || product.base64_image ? (
                                                <img src={product.thumbnail_url || product.image_url || product.base64_image} alt="" className="w-24 h-24 object-cover rounded-lg shadow-md border border-slate-200 dark:border-slate-600" loading="lazy" />
                                            ) : (
                                                <div className="w-24 h-24 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center border-2 border-dashed border-red-300 dark:border-red-700">
                                                    <Image className="h-8 w-8 text-red-400" />
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {/* Inline Edit for Name in Quick Mode */}
                                                    {inlineEdit.productId === product.product_id && inlineEdit.field === 'name' ? (
                                                        <div className="flex items-center gap-1">
                                                            <Input 
                                                                type="text" 
                                                                value={inlineEdit.value}
                                                                onChange={(e) => setInlineEdit(prev => ({ ...prev, value: e.target.value }))}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') saveInlineEdit();
                                                                    if (e.key === 'Escape') setInlineEdit({ productId: null, field: null, value: '' });
                                                                }}
                                                                className="w-48 h-7 text-sm"
                                                                autoFocus
                                                            />
                                                            <button onClick={saveInlineEdit} className="p-1 text-green-500 hover:bg-green-50 rounded">
                                                                <Check className="h-4 w-4" />
                                                            </button>
                                                            <button onClick={() => setInlineEdit({ productId: null, field: null, value: '' })} className="p-1 text-red-500 hover:bg-red-50 rounded">
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <p 
                                                            onClick={() => quickMode && product.name && setInlineEdit({ productId: product.product_id, field: 'name', value: product.name })}
                                                            className={`font-semibold text-base dark:text-white ${quickMode ? 'cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/30 px-1 rounded transition-colors' : ''}`}
                                                            title={quickMode ? 'Кликни за редактиране на името' : ''}
                                                        >
                                                            {product.name || <span className="text-red-500 italic">⚠️ Без име</span>}
                                                        </p>
                                                    )}
                                                    {isDraft && (
                                                        <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-400 dark:border-amber-700">
                                                            Чернова
                                                        </Badge>
                                                    )}
                                                </div>
                                                {/* Show weight/volume if exists */}
                                                {product.weight && (
                                                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                        📦 {product.weight} {product.weight_unit || 'гр'}
                                                    </p>
                                                )}
                                                {product.brand && <p className="text-sm text-muted-foreground dark:text-slate-400">{product.brand}</p>}
                                                {/* Show missing fields */}
                                                {hasIssues && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {issues.map((issue, i) => (
                                                            <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
                                                                ⚠️ {issue}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="space-y-1">
                                            <Select 
                                                value={product.category_id || ''} 
                                                onValueChange={(v) => quickChangeCategory(product.product_id, v)}
                                            >
                                                <SelectTrigger className="w-40 h-8 text-sm dark:bg-slate-700 dark:border-slate-600">
                                                    <SelectValue placeholder="Избери" />
                                                </SelectTrigger>
                                                <SelectContent className="dark:bg-slate-800">
                                                    {categories.filter(c => c.category_id).map(c => (
                                                        <SelectItem key={c.category_id} value={c.category_id} className="dark:text-white text-sm">
                                                            {c.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {/* Show additional categories */}
                                            {product.category_ids && product.category_ids.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {product.category_ids.slice(0, 2).map(catId => {
                                                        const cat = categories.find(c => c.category_id === catId);
                                                        return cat ? (
                                                            <Badge key={catId} variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-400">
                                                                {cat.name.substring(0, 15)}...
                                                            </Badge>
                                                        ) : null;
                                                    })}
                                                    {product.category_ids.length > 2 && (
                                                        <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-400">
                                                            +{product.category_ids.length - 2}
                                                        </Badge>
                                                    )}
                                                </div>
                                            )}
                                            {product.subcategory && (
                                                <p className="text-xs text-muted-foreground dark:text-slate-500">{product.subcategory}</p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        {/* Inline Edit for Price */}
                                        {inlineEdit.productId === product.product_id && inlineEdit.field === 'price' ? (
                                            <div className="flex items-center gap-1">
                                                <Input 
                                                    type="number" 
                                                    step="0.01"
                                                    value={inlineEdit.value}
                                                    onChange={(e) => setInlineEdit(prev => ({ ...prev, value: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') saveInlineEdit();
                                                        if (e.key === 'Escape') setInlineEdit({ productId: null, field: null, value: '' });
                                                    }}
                                                    className="w-20 h-7 text-sm"
                                                    autoFocus
                                                />
                                                <button onClick={saveInlineEdit} className="p-1 text-green-500 hover:bg-green-50 rounded">
                                                    <Check className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => setInlineEdit({ productId: null, field: null, value: '' })} className="p-1 text-red-500 hover:bg-red-50 rounded">
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div 
                                                onClick={() => quickMode && product.price && setInlineEdit({ productId: product.product_id, field: 'price', value: product.price.toString() })}
                                                className={quickMode ? 'cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/30 p-1 rounded transition-colors' : ''}
                                                title={quickMode ? 'Кликни за редактиране' : ''}
                                            >
                                                {product.price ? (
                                                    product.promotional_price ? (
                                                        <div>
                                                            <span className="font-bold text-red-600 dark:text-red-400">{product.promotional_price.toFixed(2)} €</span>
                                                            <span className="text-sm text-muted-foreground dark:text-slate-500 line-through ml-2">{product.price.toFixed(2)} €</span>
                                                        </div>
                                                    ) : (
                                                        <span className="font-medium dark:text-white">{product.price.toFixed(2)} €</span>
                                                    )
                                                ) : (
                                                    <span className="text-slate-400 italic">Без цена</span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex gap-1">
                                            <button 
                                                onClick={() => toggleFlag(product, 'is_featured')}
                                                className={`p-1.5 rounded ${product.is_featured ? 'bg-violet-100 text-violet-600 dark:bg-violet-900/50 dark:text-violet-400' : 'bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500'}`}
                                                title="Топ продукт"
                                            >
                                                <Star className="h-4 w-4" />
                                            </button>
                                            <button 
                                                onClick={() => toggleFlag(product, 'is_new')}
                                                className={`p-1.5 rounded ${product.is_new ? 'bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400' : 'bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500'}`}
                                                title="Нов"
                                            >
                                                <Sparkles className="h-4 w-4" />
                                            </button>
                                            <button 
                                                onClick={() => toggleFlag(product, 'is_bestseller')}
                                                className={`p-1.5 rounded ${product.is_bestseller ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/50 dark:text-orange-400' : 'bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500'}`}
                                                title="Бестселър"
                                            >
                                                <TrendingUp className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="icon" onClick={() => duplicateProduct(product)} title="Дублирай" className="dark:hover:bg-slate-700">
                                                <Copy className="h-4 w-4 text-blue-500" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => toggleVisibility(product)} title={product.is_visible !== false ? 'Скрий' : 'Покажи'} className="dark:hover:bg-slate-700">
                                                {product.is_visible !== false ? <Eye className="h-4 w-4 dark:text-slate-300" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                                            </Button>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" disabled={aiLoadingId === product.product_id} title="AI действия" data-testid={`ai-suggest-btn-${product.product_id}`} className="dark:hover:bg-slate-700">
                                                        <Sparkles className={`h-4 w-4 text-purple-500 ${aiLoadingId === product.product_id ? 'animate-spin' : ''}`} />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="dark:bg-slate-800 dark:border-slate-700">
                                                    <DropdownMenuLabel className="text-xs">AI действия</DropdownMenuLabel>
                                                    <DropdownMenuItem onClick={() => aiSuggestOne(product, 'fill_empty')} data-testid={`ai-mode-fill-empty-${product.product_id}`}>✨ Попълни само празните полета</DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => aiSuggestOne(product, 'title_only')} data-testid={`ai-mode-title-${product.product_id}`}>📝 Пренапиши само заглавието</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => aiSuggestOne(product, 'description_only')} data-testid={`ai-mode-desc-${product.product_id}`}>📄 Пренапиши само описанието</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => aiSuggestOne(product, 'title_description')} data-testid={`ai-mode-both-${product.product_id}`}>📃 Пренапиши заглавие и описание</DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(product)} className="dark:hover:bg-slate-700" data-testid={`edit-product-${product.product_id}`}>
                                                <Edit2 className="h-4 w-4 dark:text-slate-300" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => handleDelete(product.product_id)} className="dark:hover:bg-slate-700">
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            );
                            })}
                        </tbody>
                    </table>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t dark:border-slate-700">
                            <div className="text-sm text-muted-foreground">
                                Показване {((currentPage - 1) * productsPerPage) + 1} - {Math.min(currentPage * productsPerPage, filteredProducts.length)} от {filteredProducts.length}
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(1)} 
                                    disabled={currentPage === 1}
                                    className="dark:border-slate-600"
                                >
                                    Първа
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                                    disabled={currentPage === 1}
                                    className="dark:border-slate-600"
                                >
                                    ← Назад
                                </Button>
                                <div className="flex items-center gap-2 px-2">
                                    <span className="text-sm dark:text-slate-300">Страница</span>
                                    <Input 
                                        type="number"
                                        min={1}
                                        max={totalPages}
                                        value={currentPage}
                                        onChange={(e) => {
                                            const page = parseInt(e.target.value);
                                            if (page >= 1 && page <= totalPages) {
                                                setCurrentPage(page);
                                            }
                                        }}
                                        className="w-16 h-8 text-center dark:bg-slate-700 dark:border-slate-600"
                                    />
                                    <span className="text-sm dark:text-slate-300">от {totalPages}</span>
                                </div>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                                    disabled={currentPage === totalPages}
                                    className="dark:border-slate-600"
                                >
                                    Напред →
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(totalPages)} 
                                    disabled={currentPage === totalPages}
                                    className="dark:border-slate-600"
                                >
                                    Последна
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {/* Products Summary Footer */}
                    <div className="bg-slate-50 dark:bg-slate-800/50 border-t dark:border-slate-700 p-4 rounded-b-lg">
                        <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                            <div className="flex items-center gap-6">
                                <span className="font-medium dark:text-white">
                                    📦 Общо: <span className="text-lg font-bold text-primary">{products.length}</span> продукта
                                </span>
                                <span className="text-muted-foreground dark:text-slate-400">
                                    |  Филтрирани: <span className="font-semibold">{filteredProducts.length}</span>
                                </span>
                            </div>
                            <div className="flex items-center gap-4 text-muted-foreground dark:text-slate-400">
                                <span title="Активни продукти">
                                    ✅ {products.filter(p => !p.is_draft && p.is_visible !== false).length} активни
                                </span>
                                <span title="Чернови">
                                    📝 {products.filter(p => p.is_draft).length} чернови
                                </span>
                                <span title="С проблеми (липсващи полета)">
                                    ⚠️ {products.filter(p => getProductIssues(p).length > 0).length} с проблеми
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Orders Management
const AdminOrders = () => {
    const { token } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatingInvoice, setGeneratingInvoice] = useState(null);
    const [creatingWaybill, setCreatingWaybill] = useState(null);
    const [trackingShipment, setTrackingShipment] = useState(null);
    const [trackingData, setTrackingData] = useState(null);
    const [viewMode, setViewMode] = useState('kanban'); // 'list' or 'kanban'
    const [orderSearch, setOrderSearch] = useState('');
    const [orderStatusFilter, setOrderStatusFilter] = useState('all');
    const [orderPaymentFilter, setOrderPaymentFilter] = useState('all');
    const [expandedOrders, setExpandedOrders] = useState({});
    const [econtStatus, setEcontStatus] = useState(null);
    useEffect(() => {
        const tokenToUse = token || localStorage.getItem('token');
        axios.get(`${API}/admin/econt/status`, { headers: { Authorization: `Bearer ${tokenToUse}` } })
            .then(r => setEcontStatus(r.data)).catch(() => {});
    }, [token]);
    const [emailStatuses, setEmailStatuses] = useState({});
    const [checkingEmail, setCheckingEmail] = useState(null);
    const toggleOrderExpand = (id) => setExpandedOrders(prev => ({ ...prev, [id]: !prev[id] }));

    const checkEmailDelivery = async (orderId) => {
        const tokenToUse = token || localStorage.getItem('token');
        setCheckingEmail(orderId);
        try {
            const res = await axios.get(`${API}/admin/orders/${orderId}/email-status`, {
                headers: { Authorization: `Bearer ${tokenToUse}` }
            });
            setEmailStatuses(prev => ({ ...prev, [orderId]: res.data.emails }));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Грешка при проверка на доставката');
        } finally {
            setCheckingEmail(null);
        }
    };

    const emailEventLabel = (ev) => {
        switch (ev) {
            case 'delivered': return { text: '✓ Доставен до пощата на клиента', cls: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
            case 'opened': case 'clicked': return { text: '✓ Доставен и отворен от клиента', cls: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
            case 'bounced': return { text: '✗ ВЪРНАТ — имейл адресът не съществува', cls: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
            case 'suppressed': return { text: '✗ БЛОКИРАН — адресът е връщал писма преди (проверете за правописна грешка)', cls: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
            case 'complained': return { text: '⚠ Клиентът е маркирал писмото като спам', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' };
            case 'sent': case 'queued': case 'accepted_no_id': return { text: '⏳ Изпратен — очаква потвърждение за доставка', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
            default: return { text: '? Статусът не може да бъде проверен', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' };
        }
    };

    useEffect(() => {
        const tokenToUse = token || localStorage.getItem('token');
        if (!tokenToUse) return;
        
        const controller = new AbortController();
        
        fetch(`${API}/admin/orders`, {
            headers: { 'Authorization': `Bearer ${tokenToUse}` },
            signal: controller.signal
        })
        .then(res => res.json())
        .then(data => {
            setOrders(data.orders || []);
            setLoading(false);
        })
        .catch(err => {
            if (err.name !== 'AbortError') {
                console.error('Error:', err);
                setLoading(false);
            }
        });
        
        return () => controller.abort();
    }, [token]);

    const fetchOrders = async () => {
        const tokenToUse = token || localStorage.getItem('token');
        try {
            const response = await axios.get(`${API}/admin/orders`, {
                headers: { Authorization: `Bearer ${tokenToUse}` }
            });
            setOrders(response.data.orders);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (orderId, status) => {
        try {
            await axios.put(`${API}/admin/orders/${orderId}/status`, 
                { status },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Статусът е обновен');
            fetchOrders();
        } catch (error) {
            toast.error('Грешка');
        }
    };

    // Invoice functions
    const generateInvoice = async (orderId) => {
        setGeneratingInvoice(orderId);
        try {
            const response = await axios.post(`${API}/admin/invoices/generate/${orderId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Грешка при създаване на фактура');
        } finally {
            setGeneratingInvoice(null);
        }
    };

    const downloadInvoice = async (orderId) => {
        try {
            const response = await axios.get(`${API}/admin/invoices/${orderId}/download`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `faktura_${orderId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Фактурата е изтеглена');
        } catch (error) {
            if (error.response?.status === 404) {
                toast.error('Първо генерирайте фактурата');
            } else {
                toast.error('Грешка при изтегляне');
            }
        }
    };

    const sendInvoiceEmail = async (orderId) => {
        try {
            const response = await axios.post(`${API}/admin/invoices/${orderId}/send-email`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Грешка при изпращане');
        }
    };

    // Econt functions
    const createWaybill = async (order) => {
        setCreatingWaybill(order.order_id);
        try {
            const address = order.address || order.shipping_address || {};
            const econtDelivery = order.econt_delivery || {};
            const itemsDescription = order.items.map(i => i.name).join(', ').substring(0, 100);
            
            // Determine if delivery is to office or address
            const deliveryMethod = econtDelivery.method || address.delivery_type || 'address';
            const isOfficeDelivery = deliveryMethod === 'office';
            
            const waybillData = {
                order_id: order.order_id,
                receiver_name: address.name || 'Клиент',
                receiver_phone: address.phone || '',
                receiver_city: econtDelivery.city || address.city || 'София',
                receiver_postcode: econtDelivery.postcode || address.postal_code || address.postcode || '1000',
                receiver_address: isOfficeDelivery ? '' : (econtDelivery.streetAddress || address.address || ''),
                receiver_office_code: isOfficeDelivery ? (econtDelivery.office_code || econtDelivery.office?.code) : null,
                weight: order.total_weight || 1.5,
                items_description: itemsDescription,
                cod_amount: order.payment_method === 'cod' ? order.total : 0,
                declared_value: order.total,
                pack_count: 1
            };
            
            const response = await axios.post(`${API}/admin/econt/waybill`, waybillData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success(`Товарителница създадена: ${response.data.shipmentNumber}`);
                fetchOrders();
            } else {
                toast.error(response.data.error || 'Грешка при създаване');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Грешка при създаване на товарителница');
        } finally {
            setCreatingWaybill(null);
        }
    };

    const trackShipment = async (shipmentNumber) => {
        setTrackingShipment(shipmentNumber);
        try {
            const response = await axios.get(`${API}/admin/econt/track/${shipmentNumber}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setTrackingData(response.data);
                toast.success(`Статус: ${response.data.status}`);
            } else {
                toast.error(response.data.error);
            }
        } catch (error) {
            toast.error('Грешка при проверка на статус');
        } finally {
            setTrackingShipment(null);
        }
    };

    const deleteWaybill = async (shipmentNumber) => {
        if (!window.confirm('Сигурни ли сте, че искате да анулирате товарителницата?')) return;
        
        try {
            const response = await axios.delete(`${API}/admin/econt/waybill/${shipmentNumber}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                toast.success(response.data.message);
                fetchOrders();
            } else {
                toast.error(response.data.error);
            }
        } catch (error) {
            toast.error('Грешка при анулиране');
        }
    };

    const cancelOrder = async (orderId) => {
        if (!window.confirm('Сигурни ли сте, че искате да откажете поръчката? Наличността ще бъде възстановена.')) return;
        
        try {
            const response = await axios.post(`${API}/admin/orders/${orderId}/cancel`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Грешка при отказване');
        }
    };

    const refundOrder = async (orderId) => {
        if (!window.confirm('Сигурни ли сте, че искате да възстановите сумата? Това действие е необратимо.')) return;
        
        try {
            const response = await axios.post(`${API}/admin/orders/${orderId}/refund`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Грешка при възстановяване');
        }
    };

    const confirmPayment = async (orderId) => {
        if (!window.confirm('Потвърждавате ли, че плащането по банков превод е получено?')) return;
        
        try {
            const response = await axios.post(`${API}/admin/orders/${orderId}/confirm-payment`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Грешка при потвърждаване на плащането');
        }
    };

    const statusOptions = [
        { value: 'pending', label: 'Изчакваща' },
        { value: 'pending_payment', label: 'Незавършено плащане' },
        { value: 'awaiting_prepayment', label: 'Очаква предварително плащане' },
        { value: 'confirmed', label: 'Потвърдена' },
        { value: 'processing', label: 'Обработва се' },
        { value: 'shipped', label: 'Изпратена' },
        { value: 'delivered', label: 'Доставена' },
        { value: 'cancelled', label: 'Отказана' }
    ];

    const paymentMethodLabels = {
        'cod': 'Наложен платеж',
        'card': 'С карта',
        'bank_transfer': 'Банков превод'
    };

    const STATUS_COLORS = {
        pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
        pending_payment: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
        awaiting_prepayment: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
        confirmed: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300',
        processing: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
        shipped: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300',
        delivered: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
        cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    };
    const statusLabel = (v) => statusOptions.find(s => s.value === v)?.label || v;
    const chipCls = (active) => `px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${active ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`;

    const searchLower = orderSearch.trim().toLowerCase();
    const filteredOrders = orders.filter(o => {
        if (orderStatusFilter !== 'all' && o.status !== orderStatusFilter) return false;
        if (orderPaymentFilter !== 'all' && o.payment_method !== orderPaymentFilter) return false;
        if (searchLower) {
            const hay = [o.order_id, o.address?.name, o.address?.phone, o.user_email, o.guest_email, o.address?.city]
                .filter(Boolean).join(' ').toLowerCase();
            if (!hay.includes(searchLower)) return false;
        }
        return true;
    });
    const statusCounts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});

    const econtDemoBanner = econtStatus?.demo_mode ? (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg text-sm text-red-800 dark:text-red-300" data-testid="econt-demo-banner">
            ⚠️ <strong>ЕКОНТ ДЕМО РЕЖИМ:</strong> товарителниците се създават в тестовата система на Еконт и НЕ се появяват в реалния ви профил. Нужни са реални API данни (потребител и парола от Еконт Delivery договора ви).
        </div>
    ) : null;

    // If Kanban view is selected, render KanbanOrdersBoard
    if (viewMode === 'kanban') {
        return (
            <div>
                {econtDemoBanner}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="font-serif text-3xl font-bold dark:text-white">Поръчки</h1>
                    <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                        <Button 
                            variant={viewMode === 'kanban' ? 'default' : 'ghost'} 
                            size="sm" 
                            onClick={() => setViewMode('kanban')}
                            className="gap-2"
                            data-testid="view-kanban-btn"
                        >
                            <Kanban className="w-4 h-4" /> Kanban
                        </Button>
                        <Button 
                            variant={viewMode === 'list' ? 'default' : 'ghost'} 
                            size="sm" 
                            onClick={() => setViewMode('list')}
                            className="gap-2"
                            data-testid="view-list-btn"
                        >
                            <List className="w-4 h-4" /> Списък
                        </Button>
                    </div>
                </div>
                <KanbanOrdersBoard />
            </div>
        );
    }

    return (
        <div>
            {econtDemoBanner}
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif text-3xl font-bold dark:text-white">Поръчки</h1>
                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                    <Button 
                        variant={viewMode === 'kanban' ? 'default' : 'ghost'} 
                        size="sm" 
                        onClick={() => setViewMode('kanban')}
                        className="gap-2"
                    >
                        <Kanban className="w-4 h-4" /> Kanban
                    </Button>
                    <Button 
                        variant={viewMode === 'list' ? 'default' : 'ghost'} 
                        size="sm" 
                        onClick={() => setViewMode('list')}
                        className="gap-2"
                    >
                        <List className="w-4 h-4" /> Списък
                    </Button>
                </div>
            </div>

            {/* Search + filters toolbar */}
            {!loading && orders.length > 0 && (
                <div className="mb-4 space-y-3">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                value={orderSearch}
                                onChange={(e) => setOrderSearch(e.target.value)}
                                placeholder="Търси по № поръчка, име, телефон, имейл или град..."
                                className="pl-9 dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                                data-testid="orders-search-input"
                            />
                        </div>
                        <Select value={orderPaymentFilter} onValueChange={setOrderPaymentFilter}>
                            <SelectTrigger className="w-full sm:w-48 dark:bg-slate-800 dark:border-slate-600 dark:text-white" data-testid="orders-payment-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                <SelectItem value="all">Всички плащания</SelectItem>
                                <SelectItem value="cod">Наложен платеж</SelectItem>
                                <SelectItem value="card">С карта</SelectItem>
                                <SelectItem value="bank_transfer">Банков превод</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="orders-status-chips">
                        <button onClick={() => setOrderStatusFilter('all')} className={chipCls(orderStatusFilter === 'all')} data-testid="orders-chip-all">
                            Всички ({orders.length})
                        </button>
                        {statusOptions.map(s => (
                            <button key={s.value} onClick={() => setOrderStatusFilter(s.value)} className={chipCls(orderStatusFilter === s.value)} data-testid={`orders-chip-${s.value}`}>
                                {s.label} ({statusCounts[s.value] || 0})
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="animate-pulse space-y-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-muted dark:bg-slate-700 rounded" />)}
                </div>
            ) : orders.length === 0 ? (
                <p className="text-muted-foreground dark:text-slate-400">Няма поръчки</p>
            ) : (
                <div className="space-y-2">
                    {filteredOrders.length === 0 && (
                        <p className="text-center text-sm text-muted-foreground dark:text-slate-400 py-8" data-testid="orders-no-results">
                            Няма поръчки, отговарящи на търсенето/филтрите
                        </p>
                    )}
                    {filteredOrders.map((order) => {
                        const isOpen = !!expandedOrders[order.order_id];
                        return (
                        <div key={order.order_id} className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 overflow-hidden">
                            {/* Compact clickable row */}
                            <div
                                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                                onClick={() => toggleOrderExpand(order.order_id)}
                                data-testid={`order-row-${order.order_id}`}
                            >
                                <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                <div className="min-w-0 w-36 shrink-0">
                                    <p className="font-medium text-sm dark:text-white truncate">#{order.order_id.slice(-8).toUpperCase()}</p>
                                    <p className="text-xs text-muted-foreground dark:text-slate-400">
                                        {new Date(order.created_at).toLocaleDateString('bg-BG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                <div className="min-w-0 flex-1 hidden sm:block">
                                    <p className="text-sm dark:text-slate-200 truncate">
                                        {order.address?.name || '—'}
                                        {order.customer_info?.is_new && <span className="ml-1" title="Нов клиент">🆕</span>}
                                        {order.customer_info?.is_trusted && <span className="ml-1" title="Доверен клиент">⭐</span>}
                                    </p>
                                    <p className="text-xs text-muted-foreground dark:text-slate-400 truncate">
                                        {order.address?.city || ''} · {order.items.length} прод.
                                    </p>
                                </div>
                                <div className="hidden md:flex items-center gap-1.5 shrink-0">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                                        {statusLabel(order.status)}
                                    </span>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                        {order.payment_status === 'paid' ? '✅ Платено' : order.payment_method === 'cod' ? '💵 Наложен' : order.payment_method === 'bank_transfer' ? '🏦 Превод' : '💳 Карта'}
                                    </span>
                                    <span title={order.delivery_method === 'sofia_driver' ? 'Наш шофьор (София)' : 'Еконт'}>
                                        {order.delivery_method === 'sofia_driver' ? '🚗' : '📦'}
                                    </span>
                                    {order.econt_shipment && <span title={`Товарителница ${order.econt_shipment.shipment_number}`}>🏷️</span>}
                                </div>
                                <span className="font-bold text-sm md:text-base dark:text-white shrink-0 ml-auto md:ml-0">{order.total.toFixed(2)} €</span>
                                <div onClick={(e) => e.stopPropagation()} className="shrink-0 hidden sm:block">
                                    <Select value={order.status} onValueChange={(v) => updateStatus(order.order_id, v)}>
                                        <SelectTrigger className="w-36 h-8 text-xs dark:bg-slate-700 dark:border-slate-600 dark:text-white" data-testid={`order-status-select-${order.order_id}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                            {statusOptions.map(s => <SelectItem key={s.value} value={s.value} className="dark:text-white dark:focus:bg-slate-700">{s.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Expanded details */}
                            {isOpen && (
                            <div className="px-6 pb-6 pt-3 border-t border-border dark:border-slate-700" data-testid={`order-details-${order.order_id}`}>
                            <p className="text-xs text-muted-foreground dark:text-slate-400 mb-3">Пълен №: {order.order_id} · {new Date(order.created_at).toLocaleDateString('bg-BG', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <p className="text-sm text-muted-foreground dark:text-slate-400 mb-1">Адрес</p>
                                    <p className="text-sm dark:text-slate-300">
                                        {order.address?.name}<br />
                                        {[(order.address?.delivery_type === 'office' && !order.address?.address) ? `Офис Еконт ${order.address?.econt_office || ''}`.trim() : order.address?.address, order.address?.city].filter(Boolean).join(', ')}<br />
                                        {order.address?.phone}
                                    </p>
                                    {/* Customer Trust Badge */}
                                    {order.customer_info && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {order.customer_info.is_new ? (
                                                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                    🆕 Нов клиент
                                                </Badge>
                                            ) : order.customer_info.is_trusted ? (
                                                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                    ⭐ Доверен клиент
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                                    ⚠️ Внимание
                                                </Badge>
                                            )}
                                            <span className="text-xs text-muted-foreground dark:text-slate-400 ml-1">
                                                ({order.customer_info.delivered_orders || 0} успешни, {order.customer_info.cancelled_orders || 0} отказани)
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground dark:text-slate-400 mb-1">Плащане</p>
                                    <div className="text-sm dark:text-slate-300">
                                        {paymentMethodLabels[order.payment_method] || order.payment_method}<br />
                                        <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'} className="dark:bg-slate-600">
                                            {order.payment_status === 'paid' ? 'Платено' : order.payment_status === 'cod' ? 'При доставка' : order.requires_prepayment ? '🧊 Очаква предварително плащане' : order.payment_status === 'awaiting_transfer' ? 'Чака превод' : 'Чакащо'}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground dark:text-slate-400 mb-1 mt-3">Имейл на клиента</p>
                                    <div className="text-sm dark:text-slate-300" data-testid={`order-email-info-${order.order_id}`}>
                                        {order.user_email ? (
                                            <>
                                                <p className="font-medium break-all">{order.user_email}</p>
                                                {order.email_log?.confirmation ? (
                                                    order.email_log.confirmation.sent ? (
                                                        <Badge className="mt-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                            ✓ Потвърждение изпратено · {new Date(order.email_log.confirmation.sent_at).toLocaleString('bg-BG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="mt-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" title={order.email_log.confirmation.error || ''}>
                                                            ✗ Неуспешно изпращане ({order.email_log.confirmation.attempts} опита)
                                                        </Badge>
                                                    )
                                                ) : (
                                                    <Badge variant="secondary" className="mt-1 dark:bg-slate-600">Имейл не е изпращан още</Badge>
                                                )}
                                                {order.email_log?.status_update && (
                                                    <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                                                        {order.email_log.status_update.sent ? '✓' : '✗'} Последно известие за статус: {new Date(order.email_log.status_update.sent_at).toLocaleString('bg-BG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                )}
                                                {order.email_log && (
                                                    <div className="mt-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 text-xs"
                                                            disabled={checkingEmail === order.order_id}
                                                            onClick={() => checkEmailDelivery(order.order_id)}
                                                            data-testid={`check-email-delivery-${order.order_id}`}
                                                        >
                                                            {checkingEmail === order.order_id ? 'Проверява се...' : '📬 Провери доставката'}
                                                        </Button>
                                                        {emailStatuses[order.order_id] && (
                                                            <div className="mt-2 space-y-1" data-testid={`email-delivery-status-${order.order_id}`}>
                                                                {Object.entries(emailStatuses[order.order_id]).map(([type, info]) => {
                                                                    const label = emailEventLabel(info.last_event);
                                                                    return (
                                                                        <div key={type}>
                                                                            <span className={`inline-block text-xs px-2 py-0.5 rounded ${label.cls}`} title={info.bounce_reason || ''}>
                                                                                {type === 'confirmation' ? 'Потвърждение' : 'Статус'}: {label.text}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <p className="italic text-muted-foreground dark:text-slate-400">Не е въведен — без имейл известия</p>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground dark:text-slate-400 mb-1 mt-3">Доставка</p>
                                    <p className="text-sm dark:text-slate-300" data-testid={`order-delivery-label-${order.order_id}`}>
                                        {order.delivery_method === 'sofia_driver' 
                                            ? (order.shipping_cost > 0 
                                                ? `🚗 Доставка с наш шофьор — София — ${order.shipping_cost.toFixed(2)} €` 
                                                : '🚗 Доставка с наш шофьор — София — Безплатна')
                                            : `📦 Доставка с Еконт${order.shipping_cost > 0 ? ` — ${order.shipping_cost.toFixed(2)} €` : ''}`}
                                    </p>
                                </div>
                            </div>

                            <div className="border-t border-border dark:border-slate-700 pt-4">
                                <p className="text-sm text-muted-foreground dark:text-slate-400 mb-2">Продукти ({order.items.length})</p>
                                <div className="flex gap-2 overflow-x-auto">
                                    {order.items.map((item) => (
                                        <div key={item.product_id} className="flex items-center gap-2 bg-muted dark:bg-slate-700 p-2 rounded flex-shrink-0">
                                            <img src={item.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                                            <div className="text-sm">
                                                <p className="font-medium dark:text-white">{item.name}</p>
                                                <p className="text-muted-foreground dark:text-slate-400">x{item.quantity}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Invoice Actions */}
                            <div className="border-t border-border dark:border-slate-700 pt-4 mt-4 flex flex-wrap gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => generateInvoice(order.order_id)}
                                    disabled={generatingInvoice === order.order_id}
                                    className="dark:border-slate-600 dark:text-slate-300"
                                >
                                    {generatingInvoice === order.order_id ? '⏳ Генериране...' : '🧾 Генерирай фактура'}
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => downloadInvoice(order.order_id)}
                                    className="dark:border-slate-600 dark:text-slate-300"
                                >
                                    📥 Изтегли PDF
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => sendInvoiceEmail(order.order_id)}
                                    className="dark:border-slate-600 dark:text-slate-300"
                                >
                                    📧 Изпрати на клиент
                                </Button>
                            </div>
                            
                            {/* Shipping Actions */}
                            <div className="border-t border-border dark:border-slate-700 pt-4 mt-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${order.delivery_method === 'sofia_driver' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                                        <Truck className={`w-4 h-4 ${order.delivery_method === 'sofia_driver' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`} />
                                    </div>
                                    <h4 className="font-medium text-slate-700 dark:text-slate-200">
                                        {order.delivery_method === 'sofia_driver' ? 'Доставка с наш шофьор' : 'Еконт доставка'}
                                    </h4>
                                </div>
                                
                                {order.delivery_method === 'sofia_driver' ? (
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800" data-testid={`sofia-driver-info-${order.order_id}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                            <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                                {order.shipping_cost > 0 
                                                    ? `Доставка с наш шофьор — София — ${order.shipping_cost.toFixed(2)} €` 
                                                    : 'Доставка с наш шофьор — София — Безплатна'}
                                            </p>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-5">
                                            Не се създава товарителница в Еконт за тази поръчка.
                                        </p>
                                    </div>
                                ) : order.econt_shipment?.shipment_number && order.econt_shipment?.status !== 'error' ? (
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800" data-testid={`waybill-active-${order.order_id}`}>
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                                    <span className="text-sm font-medium text-green-700 dark:text-green-400">Товарителница активна</span>
                                                    {order.econt_shipment.demo_mode && (
                                                        <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full font-bold" data-testid={`waybill-demo-badge-${order.order_id}`} title="Създадена в ДЕМО системата на Еконт — НЕ се вижда в реалния профил!">ДЕМО</span>
                                                    )}
                                                </div>
                                                <p className="font-mono text-lg font-bold text-slate-800 dark:text-slate-200" data-testid={`waybill-number-${order.order_id}`}>
                                                    {order.econt_shipment.shipment_number}
                                                </p>
                                                <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                                                    <span className="flex items-center gap-1">
                                                        <Package className="w-3.5 h-3.5" />
                                                        {order.econt_shipment.total_price} {order.econt_shipment.currency}
                                                    </span>
                                                    {order.econt_shipment.delivery_date && (
                                                        <span className="flex items-center gap-1">
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            {order.econt_shipment.delivery_date}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => trackShipment(order.econt_shipment.shipment_number)}
                                                    disabled={trackingShipment === order.econt_shipment.shipment_number}
                                                    className="bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 gap-1.5"
                                                >
                                                    {trackingShipment === order.econt_shipment.shipment_number ? (
                                                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <MapPin className="w-3.5 h-3.5" />
                                                    )}
                                                    Проследи
                                                </Button>
                                                {order.econt_shipment.pdf_url && (
                                                    <a 
                                                        href={order.econt_shipment.pdf_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-700 rounded-md text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                                                    >
                                                        <FileText className="w-3.5 h-3.5" />
                                                        Етикет PDF
                                                    </a>
                                                )}
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => deleteWaybill(order.econt_shipment.shipment_number)}
                                                    className="bg-white dark:bg-slate-800 border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 gap-1.5"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                    Анулирай
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-dashed border-slate-300 dark:border-slate-600">
                                        <div className="flex items-center justify-between gap-4 flex-wrap">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                    <Package className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                        {order.econt_shipment?.status === 'error' ? 'Грешка при създаване на товарителница' : 'Няма товарителница'}
                                                    </p>
                                                    {order.econt_shipment?.status === 'error' && (
                                                        <p className="text-xs text-red-600 dark:text-red-400 font-medium" data-testid={`waybill-error-${order.order_id}`}>
                                                            ✗ {order.econt_shipment.last_error} ({order.econt_shipment.last_attempt_at ? new Date(order.econt_shipment.last_attempt_at).toLocaleString('bg-BG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''})
                                                        </p>
                                                    )}
                                                    {order.econt_shipment?.status === 'error' && order.econt_shipment?.shipment_number && (
                                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                                            Предишна товарителница: №{order.econt_shipment.shipment_number}{order.econt_shipment.demo_mode ? ' (ДЕМО)' : ''}
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {order.requires_prepayment && order.payment_status !== 'paid' 
                                                            ? '🧊 Изисква предварително плащане — потвърдете плащането първо' 
                                                            : order.econt_shipment?.status === 'error' ? 'Коригирайте данните и опитайте отново' : 'Създайте за да генерирате етикет'}
                                                    </p>
                                                    {order.auto_waybill_skipped === 'new_customer_cod' && !order.econt_shipment?.shipment_number && (
                                                        <p className="text-xs text-blue-600 dark:text-blue-400" data-testid={`waybill-skipped-${order.order_id}`}>
                                                            ℹ️ Наложен платеж от нов клиент — авто-товарителницата е пропусната (защита от измами). Създайте я ръчно след преценка.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <Button 
                                                onClick={() => createWaybill(order)}
                                                disabled={creatingWaybill === order.order_id || (order.requires_prepayment && order.payment_status !== 'paid')}
                                                data-testid={`create-waybill-btn-${order.order_id}`}
                                                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 gap-2"
                                            >
                                                {creatingWaybill === order.order_id ? (
                                                    <>
                                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                                        Създаване...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus className="w-4 h-4" />
                                                        Създай товарителница
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Order Actions */}
                            <div className="border-t border-border dark:border-slate-700 pt-4 mt-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                                        <Settings className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                    </div>
                                    <h4 className="font-medium text-slate-700 dark:text-slate-200">Действия</h4>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {order.requires_prepayment && order.payment_method === 'bank_transfer' && order.payment_status !== 'paid' && (
                                        <Button 
                                            size="sm"
                                            onClick={() => confirmPayment(order.order_id)}
                                            className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
                                            data-testid={`confirm-payment-btn-${order.order_id}`}
                                        >
                                            <Check className="w-3.5 h-3.5" />
                                            Плащането е получено
                                        </Button>
                                    )}
                                    {!['shipped', 'delivered', 'cancelled', 'refunded'].includes(order.status) && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => cancelOrder(order.order_id)}
                                            className="border-orange-200 dark:border-orange-700 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 gap-1.5"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                            Откажи поръчка
                                        </Button>
                                    )}
                                    {order.payment_method === 'card' && order.payment_status === 'paid' && order.refund_status !== 'refunded' && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => refundOrder(order.order_id)}
                                            className="border-purple-200 dark:border-purple-700 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 gap-1.5"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                            Възстанови сума
                                        </Button>
                                    )}
                                    <a 
                                        href={`/track/${order.order_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-200 dark:border-blue-700 rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        <Eye className="w-3.5 h-3.5" />
                                        Линк за проследяване
                                    </a>
                                </div>
                            </div>
                            </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Categories Management  
const AdminCategories = () => {
    const { token } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [slugEdited, setSlugEdited] = useState(false);
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '', slug: '', image_url: '', subcategories: [], sub_subcategories: {}, is_draft: false,
        organize_by_brands: false, require_brand: false, brand_sort: 'manual', category_brands: [], subcategory_brand_settings: {}
    });
    const [newSubcategory, setNewSubcategory] = useState('');
    const [editingSubIndex, setEditingSubIndex] = useState(null);
    const [editingSubValue, setEditingSubValue] = useState('');
    const [expandedSubs, setExpandedSubs] = useState({}); // {subName: true/false}
    const [newSubSubValue, setNewSubSubValue] = useState({}); // {subName: 'text'}

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async (showRefreshToast = false) => {
        try {
            if (showRefreshToast) setRefreshing(true);
            // Cache-buster: гарантира свежи данни независимо от browser/CDN кеш
            const response = await axios.get(`${API}/categories`, { params: { _: Date.now() } });
            setCategories(response.data);
            if (showRefreshToast) toast.success('Категориите са обновени');
        } catch (error) {
            console.error('Error:', error);
            toast.error('Грешка при зареждане на категориите');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Транслитерация на кирилица към латиница за slug
    const generateSlug = (text) => {
        const map = { 'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ж':'zh','з':'z','и':'i','й':'y','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u','ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'sht','ъ':'a','ь':'y','ю':'yu','я':'ya' };
        return text.toLowerCase().split('').map(ch => map[ch] !== undefined ? map[ch] : ch).join('')
            .replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
    };

    const handleNameChange = (value) => {
        if (!editingCategory && !slugEdited) {
            setFormData(prev => ({ ...prev, name: value, slug: generateSlug(value) }));
        } else {
            setFormData(prev => ({ ...prev, name: value }));
        }
    };

    // Move a category up (delta=-1) or down (delta=+1) in the display order.
    const moveCategory = async (index, delta) => {
        const targetIndex = index + delta;
        if (targetIndex < 0 || targetIndex >= categories.length) return;
        const next = [...categories];
        [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
        setCategories(next); // Optimistic update
        try {
            await axios.put(`${API}/admin/categories/reorder`,
                { category_ids: next.map(c => c.category_id) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (error) {
            console.error('Reorder error:', error);
            toast.error('Грешка при пренареждане');
            await fetchCategories(); // Revert on failure
        }
    };

    const handleSubmit = async (e, saveAsDraft = false) => {
        e?.preventDefault();
        if (saving) return;
        if (!saveAsDraft) {
            if (!formData.name.trim()) { toast.error('Името на категорията е задължително'); return; }
            if (!formData.slug.trim()) { toast.error('Slug полето е задължително'); return; }
        }
        setSaving(true);
        try {
            const data = {
                ...formData,
                subcategories: formData.subcategories.filter(s => s.trim()),
                sub_subcategories: formData.sub_subcategories || {},
                is_draft: saveAsDraft
            };

            if (editingCategory) {
                await axios.put(`${API}/admin/categories/${editingCategory.category_id}`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(saveAsDraft ? 'Черновата е запазена' : 'Категорията е обновена');
            } else {
                await axios.post(`${API}/admin/categories`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(saveAsDraft ? 'Черновата е запазена' : 'Категорията е създадена');
            }
            setShowDialog(false);
            resetForm();
            await fetchCategories();
        } catch (error) {
            console.error('Error saving category:', error.response?.data || error);
            toast.error(formatApiError(error, 'Грешка при запазване на категорията.'));
            // Формата остава отворена и данните се запазват при грешка
        } finally {
            setSaving(false);
        }
    };

    // Persist subcategories immediately when editing an existing category.
    // Returns true on success (or when no persist needed for a new draft).
    const persistSubcategories = async (nextSubs, nextSubSubs = null, nextSubBrandSettings = null) => {
        if (!editingCategory) return true;
        try {
            const subSubs = nextSubSubs !== null ? nextSubSubs : (formData.sub_subcategories || {});
            const subBrand = nextSubBrandSettings !== null ? nextSubBrandSettings : (formData.subcategory_brand_settings || {});
            await axios.put(`${API}/admin/categories/${editingCategory.category_id}`, {
                name: editingCategory.name,
                slug: editingCategory.slug,
                image_url: editingCategory.image_url || '',
                parent_id: editingCategory.parent_id || null,
                subcategories: nextSubs,
                sub_subcategories: subSubs,
                is_draft: editingCategory.is_draft || false,
                organize_by_brands: formData.organize_by_brands || false,
                require_brand: formData.require_brand || false,
                brand_sort: formData.brand_sort || 'manual',
                category_brands: formData.category_brands || [],
                subcategory_brand_settings: subBrand,
            }, { headers: { Authorization: `Bearer ${token}` } });
            // Keep local editingCategory in sync so subsequent edits use latest data
            setEditingCategory(prev => prev ? { ...prev, subcategories: nextSubs, sub_subcategories: subSubs, subcategory_brand_settings: subBrand } : prev);
            await fetchCategories();
            return true;
        } catch (error) {
            console.error('Error persisting subcategories:', error);
            const errorMsg = error.response?.data?.detail || 'Грешка при запазване на подкатегорията';
            toast.error(typeof errorMsg === 'string' ? errorMsg : 'Грешка при запазване');
            return false;
        }
    };

    // Add new subcategory
    const addSubcategory = async () => {
        if (!newSubcategory.trim()) return;
        if (formData.subcategories.includes(newSubcategory.trim())) {
            toast.error('Тази подкатегория вече съществува');
            return;
        }
        const nextSubs = [...formData.subcategories, newSubcategory.trim()];
        setFormData({ ...formData, subcategories: nextSubs });
        setNewSubcategory('');
        const ok = await persistSubcategories(nextSubs);
        if (ok && editingCategory) toast.success('Подкатегорията е добавена');
    };

    // Remove subcategory
    const removeSubcategory = async (index) => {
        const removed = formData.subcategories[index];
        const nextSubs = formData.subcategories.filter((_, i) => i !== index);
        const nextSubSubs = { ...(formData.sub_subcategories || {}) };
        delete nextSubSubs[removed];
        const nextSubBrand = { ...(formData.subcategory_brand_settings || {}) };
        delete nextSubBrand[removed];
        setFormData({ ...formData, subcategories: nextSubs, sub_subcategories: nextSubSubs, subcategory_brand_settings: nextSubBrand });
        const ok = await persistSubcategories(nextSubs, nextSubSubs, nextSubBrand);
        if (ok && editingCategory) toast.success(`Подкатегорията "${removed}" е изтрита`);
    };

    // Start editing subcategory
    const startEditSubcategory = (index) => {
        setEditingSubIndex(index);
        setEditingSubValue(formData.subcategories[index]);
    };

    // Save edited subcategory
    const saveEditSubcategory = async () => {
        if (!editingSubValue.trim()) {
            const idx = editingSubIndex;
            setEditingSubIndex(null);
            setEditingSubValue('');
            await removeSubcategory(idx);
            return;
        }
        const oldName = formData.subcategories[editingSubIndex];
        const newName = editingSubValue.trim();
        const newSubs = [...formData.subcategories];
        newSubs[editingSubIndex] = newName;
        // Migrate sub_subcategories key if the sub name changed
        const newSubSubs = { ...(formData.sub_subcategories || {}) };
        if (oldName !== newName && newSubSubs[oldName]) {
            newSubSubs[newName] = newSubSubs[oldName];
            delete newSubSubs[oldName];
        }
        // Migrate per-subcategory brand settings key
        const newSubBrand = { ...(formData.subcategory_brand_settings || {}) };
        if (oldName !== newName && newSubBrand[oldName]) {
            newSubBrand[newName] = newSubBrand[oldName];
            delete newSubBrand[oldName];
        }
        setFormData({ ...formData, subcategories: newSubs, sub_subcategories: newSubSubs, subcategory_brand_settings: newSubBrand });
        setEditingSubIndex(null);
        setEditingSubValue('');
        const ok = await persistSubcategories(newSubs, newSubSubs, newSubBrand);
        if (ok && editingCategory) toast.success('Подкатегорията е обновена');
    };

    // ==== SUB-SUBCATEGORIES (level 3) ====
    const toggleExpandSub = (subName) => {
        setExpandedSubs(prev => ({ ...prev, [subName]: !prev[subName] }));
    };

    const addSubSubcategory = async (subName) => {
        const value = (newSubSubValue[subName] || '').trim();
        if (!value) return;
        const existing = (formData.sub_subcategories?.[subName]) || [];
        if (existing.includes(value)) {
            toast.error('Тази под-подкатегория вече съществува');
            return;
        }
        const nextSubSubs = { ...(formData.sub_subcategories || {}), [subName]: [...existing, value] };
        setFormData({ ...formData, sub_subcategories: nextSubSubs });
        setNewSubSubValue(prev => ({ ...prev, [subName]: '' }));
        const ok = await persistSubcategories(formData.subcategories, nextSubSubs);
        if (ok && editingCategory) toast.success('Под-подкатегорията е добавена');
    };

    const removeSubSubcategory = async (subName, ssName) => {
        const existing = (formData.sub_subcategories?.[subName]) || [];
        const nextArr = existing.filter(x => x !== ssName);
        const nextSubSubs = { ...(formData.sub_subcategories || {}) };
        if (nextArr.length) nextSubSubs[subName] = nextArr; else delete nextSubSubs[subName];
        setFormData({ ...formData, sub_subcategories: nextSubSubs });
        const ok = await persistSubcategories(formData.subcategories, nextSubSubs);
        if (ok && editingCategory) toast.success(`"${ssName}" е изтрита`);
    };

    const handleDelete = async (categoryId) => {
        if (!window.confirm('Сигурни ли сте? Това ще засегне всички продукти в тази категория.')) return;
        try {
            await axios.delete(`${API}/admin/categories/${categoryId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Категорията е изтрита');
            // Обновяване на списъка локално за по-бърз отговор
            setCategories(prev => prev.filter(c => c.category_id !== categoryId));
            // След това обновяване от сървъра за сигурност
            await fetchCategories();
        } catch (error) {
            console.error('Error:', error);
            const errorMsg = error.response?.data?.detail || 'Грешка при изтриване';
            toast.error(errorMsg);
            // Обновяване при грешка за да се върне правилното състояние
            await fetchCategories();
        }
    };

    const openEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name || '',
            slug: category.slug || '',
            image_url: category.image_url || '',
            subcategories: category.subcategories || [],
            sub_subcategories: category.sub_subcategories || {},
            is_draft: category.is_draft || false,
            organize_by_brands: category.organize_by_brands || false,
            require_brand: category.require_brand || false,
            brand_sort: category.brand_sort || 'manual',
            category_brands: category.category_brands || [],
            subcategory_brand_settings: category.subcategory_brand_settings || {}
        });
        setExpandedSubs({});
        setNewSubSubValue({});
        setShowDialog(true);
    };

    const resetForm = () => {
        setEditingCategory(null);
        setFormData({ name: '', slug: '', image_url: '', subcategories: [], sub_subcategories: {}, is_draft: false, organize_by_brands: false, require_brand: false, brand_sort: 'manual', category_brands: [], subcategory_brand_settings: {} });
        setNewSubcategory('');
        setEditingSubIndex(null);
        setEditingSubValue('');
        setExpandedSubs({});
        setNewSubSubValue({});
        setSlugEdited(false);
    };

    // Handle file upload for category image
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Моля, изберете изображение');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Файлът е твърде голям (макс. 5MB)');
            return;
        }

        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        try {
            const response = await axios.post(`${API}/admin/upload-image`, uploadFormData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setFormData({ ...formData, image_url: response.data.url });
            toast.success('Снимката е качена');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Грешка при качване на снимката');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <h1 className="font-serif text-3xl font-bold dark:text-white">Категории</h1>
                    <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => fetchCategories(true)}
                        disabled={refreshing}
                        className="dark:border-slate-600 dark:hover:bg-slate-700"
                        title="Обнови категориите"
                    >
                        <RefreshCw className={`h-4 w-4 dark:text-slate-300 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
                <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-category-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Добави категория
                        </Button>
                    </DialogTrigger>
                    <DialogContent onCloseAutoFocus={(e) => e.preventDefault()} className="dark:bg-slate-800 dark:border-slate-700 max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="dark:text-white">{editingCategory ? 'Редактирай категория' : 'Нова категория'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label className="dark:text-slate-300">Име *</Label>
                                <Input value={formData.name} onChange={(e) => handleNameChange(e.target.value)} required data-testid="category-name-input" className="dark:bg-slate-700 dark:border-slate-600" />
                            </div>
                            <div>
                                <Label className="dark:text-slate-300">Slug *</Label>
                                <Input value={formData.slug} onChange={(e) => { setSlugEdited(true); setFormData({ ...formData, slug: e.target.value }); }} required placeholder="food, cleaning, etc." data-testid="category-slug-input" className="dark:bg-slate-700 dark:border-slate-600" />
                                <p className="text-xs text-muted-foreground dark:text-slate-500 mt-1">Генерира се автоматично от името. Може да го редактирате.</p>
                            </div>
                            <div>
                                <Label className="dark:text-slate-300">Снимка на категорията</Label>
                                <div className="mt-2 space-y-3">
                                    {/* Image Preview */}
                                    {formData.image_url && (
                                        <div className="relative w-32 h-32 rounded-lg overflow-hidden border dark:border-slate-600">
                                            <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, image_url: '' })}
                                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Upload Button */}
                                    <div className="flex gap-2">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            accept="image/*"
                                            className="hidden"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="flex-1 dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
                                        >
                                            {uploading ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                                                    Качване...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    {formData.image_url ? 'Смени снимката' : 'Качи снимка'}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground dark:text-slate-500">
                                        Поддържани формати: JPG, PNG, WebP (макс. 5MB)
                                    </p>
                                </div>
                            </div>
                            <div>
                                <Label className="dark:text-slate-300">Подкатегории</Label>
                                <div className="mt-2 space-y-3">
                                    {/* Add new subcategory */}
                                    <div className="flex gap-2">
                                        <Input 
                                            value={newSubcategory} 
                                            onChange={(e) => setNewSubcategory(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubcategory())}
                                            placeholder="Добави нова подкатегория..."
                                            className="flex-1 dark:bg-slate-700 dark:border-slate-600"
                                        />
                                        <Button type="button" onClick={addSubcategory} variant="outline" className="dark:border-slate-600 dark:text-white">
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    
                                    {/* List of subcategories */}
                                    {formData.subcategories.length > 0 ? (
                                        <div className="space-y-2 max-h-96 overflow-y-auto">
                                            {formData.subcategories.map((sub, index) => {
                                                const subSubs = (formData.sub_subcategories && formData.sub_subcategories[sub]) || [];
                                                const isExpanded = !!expandedSubs[sub];
                                                return (
                                                <div key={`subcat-${sub}-${index}`} className="bg-slate-100 dark:bg-slate-700 rounded-lg">
                                                    <div className="flex items-center gap-2 p-2">
                                                        {editingSubIndex === index ? (
                                                            <>
                                                                <Input
                                                                    value={editingSubValue}
                                                                    onChange={(e) => setEditingSubValue(e.target.value)}
                                                                    onKeyPress={(e) => e.key === 'Enter' && saveEditSubcategory()}
                                                                    className="flex-1 h-8 dark:bg-slate-600 dark:border-slate-500"
                                                                    autoFocus
                                                                />
                                                                <Button type="button" size="sm" variant="ghost" onClick={saveEditSubcategory} className="h-8 w-8 p-0">
                                                                    <Check className="h-4 w-4 text-green-500" />
                                                                </Button>
                                                                <Button type="button" size="sm" variant="ghost" onClick={() => setEditingSubIndex(null)} className="h-8 w-8 p-0">
                                                                    <X className="h-4 w-4 text-red-500" />
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => toggleExpandSub(sub)}
                                                                    className="h-8 w-8 p-0 dark:hover:bg-slate-600"
                                                                    title={isExpanded ? 'Скрий' : 'Покажи под-подкатегории'}
                                                                    data-testid={`toggle-subsub-${sub}`}
                                                                >
                                                                    {isExpanded ? <ChevronDown className="h-4 w-4 dark:text-slate-300" /> : <ChevronRight className="h-4 w-4 dark:text-slate-300" />}
                                                                </Button>
                                                                <span className="flex-1 text-sm dark:text-white">{sub}</span>
                                                                {subSubs.length > 0 && (
                                                                    <Badge variant="outline" className="text-xs dark:border-slate-500 dark:text-slate-300">
                                                                        {subSubs.length}
                                                                    </Badge>
                                                                )}
                                                                <Button type="button" size="sm" variant="ghost" onClick={() => startEditSubcategory(index)} className="h-8 w-8 p-0 dark:hover:bg-slate-600">
                                                                    <Edit2 className="h-3 w-3 dark:text-slate-400" />
                                                                </Button>
                                                                <Button type="button" size="sm" variant="ghost" onClick={() => removeSubcategory(index)} className="h-8 w-8 p-0 dark:hover:bg-slate-600">
                                                                    <Trash2 className="h-3 w-3 text-red-500" />
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="px-3 pb-3 pt-1 border-t border-slate-200 dark:border-slate-600">
                                                            <p className="text-xs text-muted-foreground dark:text-slate-400 mb-2">Под-подкатегории</p>
                                                            <div className="flex gap-2 mb-2">
                                                                <Input
                                                                    value={newSubSubValue[sub] || ''}
                                                                    onChange={(e) => setNewSubSubValue(prev => ({ ...prev, [sub]: e.target.value }))}
                                                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubSubcategory(sub))}
                                                                    placeholder={`Напр. Кашкавал, Сирене...`}
                                                                    className="flex-1 h-8 text-sm dark:bg-slate-600 dark:border-slate-500"
                                                                    data-testid={`subsub-input-${sub}`}
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    onClick={() => addSubSubcategory(sub)}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="h-8 dark:border-slate-500 dark:text-white"
                                                                    data-testid={`add-subsub-${sub}`}
                                                                >
                                                                    <Plus className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                            {subSubs.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {subSubs.map((ss) => (
                                                                        <div key={ss} className="flex items-center gap-1 bg-white dark:bg-slate-800 px-2 py-1 rounded border dark:border-slate-500 text-xs">
                                                                            <span className="dark:text-slate-200">{ss}</span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeSubSubcategory(sub, ss)}
                                                                                className="text-red-500 hover:text-red-600"
                                                                                title="Изтрий"
                                                                            >
                                                                                <X className="h-3 w-3" />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className="text-xs text-muted-foreground dark:text-slate-500 italic">Няма добавени под-подкатегории</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground dark:text-slate-500 text-center py-4">
                                            Няма добавени подкатегории
                                        </p>
                                    )}
                                </div>
                            </div>
                            <CategoryBrandsSection formData={formData} setFormData={setFormData} token={token} />
                            <div className="flex gap-3">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    className="flex-1 dark:border-slate-600 dark:text-slate-300"
                                    onClick={(e) => handleSubmit(e, true)}
                                    disabled={saving}
                                    data-testid="save-category-draft-btn"
                                >
                                    {saving ? 'Запазване...' : '💾 Запази като чернова'}
                                </Button>
                                <Button type="submit" className="flex-1 btn-primary" disabled={saving} data-testid="save-category-btn">
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                                            Запазване...
                                        </>
                                    ) : (
                                        editingCategory ? 'Запази промените' : 'Създай категория'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="animate-pulse space-y-3">
                    {[1, 2].map(i => <div key={i} className="h-32 bg-muted dark:bg-slate-700 rounded" />)}
                </div>
            ) : categories.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 p-12 text-center">
                    <FolderTree className="h-12 w-12 text-muted-foreground dark:text-slate-500 mx-auto mb-4" />
                    <p className="text-muted-foreground dark:text-slate-400">Няма добавени категории</p>
                    <p className="text-sm text-muted-foreground dark:text-slate-500 mt-1">Добавете категория за да организирате продуктите</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {categories.map((cat, catIndex) => {
                        const isDraft = cat.is_draft || !cat.name || !cat.slug;
                        const isFirst = catIndex === 0;
                        const isLast = catIndex === categories.length - 1;
                        return (
                        <div key={cat.category_id} className={`bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 overflow-hidden ${isDraft ? 'border-amber-400 dark:border-amber-600' : ''}`}>
                            {/* Category Header */}
                            <div className="p-6 flex items-start justify-between">
                                <div className="flex items-start gap-4">
                                    {/* Reorder controls */}
                                    <div className="flex flex-col gap-1 pt-1">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-7 w-7 dark:border-slate-600 dark:hover:bg-slate-700"
                                            disabled={isFirst}
                                            onClick={() => moveCategory(catIndex, -1)}
                                            title="Премести нагоре"
                                            data-testid={`move-category-up-${cat.slug || cat.category_id}`}
                                        >
                                            <ArrowUp className="h-3.5 w-3.5 dark:text-slate-300" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-7 w-7 dark:border-slate-600 dark:hover:bg-slate-700"
                                            disabled={isLast}
                                            onClick={() => moveCategory(catIndex, 1)}
                                            title="Премести надолу"
                                            data-testid={`move-category-down-${cat.slug || cat.category_id}`}
                                        >
                                            <ArrowDown className="h-3.5 w-3.5 dark:text-slate-300" />
                                        </Button>
                                    </div>
                                    {cat.image_url ? (
                                        <img src={cat.image_url} alt="" className="w-20 h-20 object-cover rounded-lg shadow-sm" />
                                    ) : (
                                        <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                                            <FolderTree className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                                        </div>
                                    )}
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-serif text-xl font-bold dark:text-white">{cat.name || <span className="text-slate-400 italic">Без име</span>}</h3>
                                            {isDraft && (
                                                <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-400 dark:border-amber-700">
                                                    ⚠️ Чернова
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground dark:text-slate-400 mb-2">/{cat.slug || <span className="italic">без-slug</span>}</p>
                                        <Badge variant="outline" className="dark:border-slate-600 dark:text-slate-400">
                                            {cat.subcategories?.length || 0} подкатегории
                                        </Badge>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => openEdit(cat)} className="dark:border-slate-600 dark:text-white dark:hover:bg-slate-700">
                                        <Edit2 className="h-4 w-4 mr-2" />
                                        Редактирай
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.category_id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Subcategories Section */}
                            {cat.subcategories?.length > 0 && (
                                <div className="px-6 pb-6 pt-0">
                                    <p className="text-xs font-medium text-muted-foreground dark:text-slate-500 mb-2 uppercase tracking-wider">Подкатегории</p>
                                    <div className="flex flex-wrap gap-2">
                                        {cat.subcategories.map((sub) => (
                                            <Badge 
                                                key={sub} 
                                                variant="secondary" 
                                                className="dark:bg-slate-700 dark:text-slate-300 py-1.5 px-3"
                                            >
                                                {sub}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                    })}
                </div>
            )}
        </div>
    );
};

// Users Management
const AdminUsers = () => {
    const { token } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const tokenToUse = token || localStorage.getItem('token');
        if (!tokenToUse) return;
        
        const controller = new AbortController();
        
        fetch(`${API}/admin/users`, {
            headers: { 'Authorization': `Bearer ${tokenToUse}` },
            signal: controller.signal
        })
        .then(res => res.json())
        .then(data => {
            setUsers(data.users || []);
            setLoading(false);
        })
        .catch(err => {
            if (err.name !== 'AbortError') {
                console.error('Error:', err);
                setLoading(false);
            }
        });
        
        return () => controller.abort();
    }, [token]);

    const fetchUsers = async () => {
        const tokenToUse = token || localStorage.getItem('token');
        try {
            const response = await axios.get(`${API}/admin/users`, {
                headers: { Authorization: `Bearer ${tokenToUse}` }
            });
            setUsers(response.data.users);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateRole = async (userId, role) => {
        try {
            await axios.put(`${API}/admin/users/${userId}/role`,
                { role },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Ролята е обновена');
            fetchUsers();
        } catch (error) {
            toast.error('Грешка');
        }
    };

    return (
        <div>
            <h1 className="font-serif text-3xl font-bold mb-8 dark:text-white">Потребители</h1>

            {loading ? (
                <div className="animate-pulse space-y-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted dark:bg-slate-700 rounded" />)}
                </div>
            ) : (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-muted dark:bg-slate-700">
                            <tr>
                                <th className="text-left p-4 dark:text-slate-200">Потребител</th>
                                <th className="text-left p-4 dark:text-slate-200">Имейл</th>
                                <th className="text-left p-4 dark:text-slate-200">Роля</th>
                                <th className="text-left p-4 dark:text-slate-200">Дата</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.user_id} className="border-t border-border dark:border-slate-700">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            {user.picture ? (
                                                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-muted dark:bg-slate-700 flex items-center justify-center">
                                                    <Users className="h-5 w-5 text-muted-foreground dark:text-slate-500" />
                                                </div>
                                            )}
                                            <span className="font-medium dark:text-white">{user.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-muted-foreground dark:text-slate-400">{user.email}</td>
                                    <td className="p-4">
                                        <Select value={user.role} onValueChange={(v) => updateRole(user.user_id, v)}>
                                            <SelectTrigger className="w-32 dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                                <SelectItem value="user" className="dark:text-white dark:focus:bg-slate-700">User</SelectItem>
                                                <SelectItem value="admin" className="dark:text-white dark:focus:bg-slate-700">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="p-4 text-sm text-muted-foreground dark:text-slate-400">
                                        {new Date(user.created_at).toLocaleDateString('bg-BG')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// Bank Details Management
const AdminBankDetails = () => {
    const { token } = useAuth();
    const [bankDetails, setBankDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingBank, setEditingBank] = useState(null);
    const [formData, setFormData] = useState({
        account_holder: '', iban: '', bic: '', bank_name: '', is_active: true
    });

    useEffect(() => {
        const tokenToUse = token || localStorage.getItem('token');
        if (!tokenToUse) return;
        
        const controller = new AbortController();
        
        fetch(`${API}/admin/bank-details`, {
            headers: { 'Authorization': `Bearer ${tokenToUse}` },
            signal: controller.signal
        })
        .then(res => res.json())
        .then(data => {
            setBankDetails(data || []);
            setLoading(false);
        })
        .catch(err => {
            if (err.name !== 'AbortError') {
                console.error('Error:', err);
                setLoading(false);
            }
        });
        
        return () => controller.abort();
    }, [token]);

    const fetchBankDetails = async () => {
        const tokenToUse = token || localStorage.getItem('token');
        try {
            const response = await axios.get(`${API}/admin/bank-details`, {
                headers: { Authorization: `Bearer ${tokenToUse}` }
            });
            setBankDetails(response.data);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingBank) {
                await axios.put(`${API}/admin/bank-details/${editingBank.bank_id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Банковите данни са обновени');
            } else {
                await axios.post(`${API}/admin/bank-details`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Банковите данни са добавени');
            }
            setShowDialog(false);
            resetForm();
            fetchBankDetails();
        } catch (error) {
            toast.error('Грешка при запазване');
        }
    };

    const handleDelete = async (bankId) => {
        if (!window.confirm('Сигурни ли сте?')) return;
        try {
            await axios.delete(`${API}/admin/bank-details/${bankId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Банковите данни са изтрити');
            fetchBankDetails();
        } catch (error) {
            toast.error('Грешка при изтриване');
        }
    };

    const openEdit = (bank) => {
        setEditingBank(bank);
        setFormData({
            account_holder: bank.account_holder,
            iban: bank.iban,
            bic: bank.bic,
            bank_name: bank.bank_name,
            is_active: bank.is_active
        });
        setShowDialog(true);
    };

    const resetForm = () => {
        setEditingBank(null);
        setFormData({ account_holder: '', iban: '', bic: '', bank_name: '', is_active: true });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-serif text-3xl font-bold dark:text-white">Банкови данни</h1>
                    <p className="text-muted-foreground dark:text-slate-400 mt-1">Управлявайте банковите сметки за плащане по банков път</p>
                </div>
                <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-bank-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Добави сметка
                        </Button>
                    </DialogTrigger>
                    <DialogContent onCloseAutoFocus={(e) => e.preventDefault()} className="dark:bg-slate-800 dark:border-slate-700">
                        <DialogHeader>
                            <DialogTitle className="dark:text-white">{editingBank ? 'Редактирай банкови данни' : 'Нова банкова сметка'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label>Титуляр на сметката *</Label>
                                <Input 
                                    value={formData.account_holder} 
                                    onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })} 
                                    required 
                                    placeholder="Вкусът на Турция ЕООД"
                                    data-testid="bank-holder-input"
                                />
                            </div>
                            <div>
                                <Label>IBAN *</Label>
                                <Input 
                                    value={formData.iban} 
                                    onChange={(e) => setFormData({ ...formData, iban: e.target.value })} 
                                    required 
                                    placeholder="BG12UNCR12345678901234"
                                    data-testid="bank-iban-input"
                                />
                            </div>
                            <div>
                                <Label>BIC код *</Label>
                                <Input 
                                    value={formData.bic} 
                                    onChange={(e) => setFormData({ ...formData, bic: e.target.value })} 
                                    required 
                                    placeholder="UNCRBGSF"
                                    data-testid="bank-bic-input"
                                />
                            </div>
                            <div>
                                <Label>Име на банката *</Label>
                                <Input 
                                    value={formData.bank_name} 
                                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} 
                                    required 
                                    placeholder="УниКредит Булбанк"
                                    data-testid="bank-name-input"
                                />
                            </div>
                            <label className="flex items-center gap-2">
                                <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData({ ...formData, is_active: c })} />
                                <span>Активна (показва се при поръчка)</span>
                            </label>
                            <Button type="submit" className="w-full btn-primary" data-testid="save-bank-btn">
                                {editingBank ? 'Запази промените' : 'Добави сметка'}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="animate-pulse space-y-3">
                    <div className="h-32 bg-muted dark:bg-slate-700 rounded" />
                </div>
            ) : bankDetails.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 p-8 text-center">
                    <Banknote className="h-12 w-12 text-muted-foreground dark:text-slate-500 mx-auto mb-4" />
                    <p className="text-muted-foreground dark:text-slate-400">Няма добавени банкови сметки</p>
                    <p className="text-sm text-muted-foreground dark:text-slate-500 mt-1">Добавете сметка, за да активирате плащане по банков път</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {bankDetails.map((bank) => (
                        <div key={bank.bank_id} className={`bg-white dark:bg-slate-800 rounded-xl border p-6 ${bank.is_active ? 'border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-900/20' : 'border-border dark:border-slate-700'}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="font-semibold text-lg dark:text-white">{bank.bank_name}</h3>
                                        {bank.is_active && (
                                            <Badge variant="default" className="bg-green-600">Активна</Badge>
                                        )}
                                    </div>
                                    <p className="text-muted-foreground dark:text-slate-400 mb-1">{bank.account_holder}</p>
                                    <p className="font-mono text-sm dark:text-slate-300">IBAN: {bank.iban}</p>
                                    <p className="font-mono text-sm dark:text-slate-300">BIC: {bank.bic}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="icon" onClick={() => openEdit(bank)} className="dark:hover:bg-slate-700">
                                        <Edit2 className="h-4 w-4 dark:text-slate-300" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(bank.bank_id)} className="dark:hover:bg-slate-700">
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Sofia Delivery Settings
const AdminDeliverySettings = () => {
    const { token } = useAuth();
    const [settings, setSettings] = useState({ enabled: true, threshold: 25, fee_above_threshold: 0 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const tokenToUse = token || localStorage.getItem('token');
        if (!tokenToUse) return;
        axios.get(`${API}/admin/settings/sofia-delivery`, {
            headers: { Authorization: `Bearer ${tokenToUse}` }
        })
        .then(res => setSettings(res.data))
        .catch(err => console.error('Error:', err))
        .finally(() => setLoading(false));
    }, [token]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const tokenToUse = token || localStorage.getItem('token');
            const res = await axios.put(`${API}/admin/settings/sofia-delivery`, {
                enabled: settings.enabled,
                threshold: parseFloat(settings.threshold) || 0,
                fee_above_threshold: parseFloat(settings.fee_above_threshold) || 0
            }, {
                headers: { Authorization: `Bearer ${tokenToUse}` }
            });
            setSettings(res.data);
            toast.success('Настройките за доставка са запазени');
        } catch (error) {
            toast.error('Грешка при запазване');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="mb-8">
                <h1 className="font-serif text-3xl font-bold dark:text-white">Настройки за доставка</h1>
                <p className="text-muted-foreground dark:text-slate-400 mt-1">Доставка в гр. София с наш шофьор</p>
            </div>

            {loading ? (
                <div className="animate-pulse h-64 bg-muted dark:bg-slate-700 rounded-xl" />
            ) : (
                <form onSubmit={handleSave} className="bg-white dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 p-6 max-w-xl space-y-6">
                    <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <div>
                            <p className="font-medium dark:text-white">Доставка в София с наш шофьор</p>
                            <p className="text-sm text-muted-foreground dark:text-slate-400">
                                Когато е активно, поръчките в гр. София не използват Еконт
                            </p>
                        </div>
                        <Switch 
                            checked={settings.enabled} 
                            onCheckedChange={(c) => setSettings({ ...settings, enabled: c })} 
                            data-testid="sofia-delivery-enabled-switch"
                        />
                    </div>

                    <div>
                        <Label>Праг за безплатна доставка (€)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={settings.threshold}
                            onChange={(e) => setSettings({ ...settings, threshold: e.target.value })}
                            className="mt-1"
                            data-testid="sofia-delivery-threshold-input"
                        />
                        <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                            Поръчки до и включително тази сума са с безплатна доставка с наш шофьор
                        </p>
                    </div>

                    <div>
                        <Label>Такса за доставка над прага (€)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={settings.fee_above_threshold}
                            onChange={(e) => setSettings({ ...settings, fee_above_threshold: e.target.value })}
                            className="mt-1"
                            data-testid="sofia-delivery-fee-input"
                        />
                        <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                            0.00 € = винаги безплатна доставка в София
                        </p>
                    </div>

                    <Button type="submit" disabled={saving} className="btn-primary w-full" data-testid="save-delivery-settings-btn">
                        {saving ? 'Запазване...' : 'Запази настройките'}
                    </Button>
                </form>
            )}
        </div>
    );
};

// Main Admin Page with Routes
const AdminPage = () => {
    return (
        <AdminLayout>
            <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="brands" element={<BrandsManager />} />
                <Route path="promotions" element={<PromotionsManager />} />
                <Route path="backup" element={<BackupManager />} />
                <Route path="icash" element={<ICashManager />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="bank" element={<AdminBankDetails />} />
                <Route path="delivery" element={<AdminDeliverySettings />} />
            </Routes>
        </AdminLayout>
    );
};

export default AdminPage;
